// @src/pre-deployment/src/data/DataService.js v1.4
// - Central data service for all Firebase operations
// - Handles grammar, vocabulary, lessons, exercises, practice, and tracking
// - Provides unified interface for all components
// - Includes caching, error handling, and performance optimization
// - ENHANCED: Request deduplication, retry logic, offline support, better error handling
// - NEW: Learning modules order management with new document structure
// - NEW: Integrated component scanner for dynamic module discovery
// - ENHANCED v1.4: Added validation layer with ValidationError throwing (not silent masking)

import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
  setDoc,
  serverTimestamp,
  orderBy,
  limit,
  updateDoc
} from 'firebase/firestore';

import { writeRawError } from '../components/ErrorHandling/AutomatedErrorSystem/RawErrorService';
import { db, auth } from '../services/firebase.js';
import { ContentDiscoveryService } from '../tracking/contentDiscovery.js';
import { TrackingV2Service } from '../tracking/trackingService.js';
import { getDefaultModuleMap, mergeModuleMappings, scanComponentsDirectory, validateModuleMapping } from '../utils/componentScanner.js';
import {
  ValidationError,
  validateGrammarPointsResult,
  validateVocabularyArray,
  validateDialogueData,
  validateDialogueArray
} from '../utils/validationSchemas.js';

import { LEVEL_2_GRAMMAR_SCHEMA, GrammarSchemaUtils } from './grammarSchema.js';
import learningModulesOrderData from './LearningModulesOrder.json' with { type: 'json' };
import { UserGrammarTracking } from './UserGrammarTracking.js';

// Conditionally import pushLog - only available in browser (React component)
// In Node.js, use a no-op function
let pushLog = () => { }; // Default no-op for Node.js
if (typeof window !== 'undefined') {
  // Dynamic import in browser - will be evaluated at runtime
  import('../components/shared/ConsoleOverlay.js').then(module => {
    pushLog = module.pushLog;
  }).catch(() => {
    // Silently fail if ConsoleOverlay not available
  });
}

// Note: React Query hooks moved to useDataService.js


// Enhanced cache configuration
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 1000; // 1 second

// Silent error wrapper - ONLY for non-critical operations (tracking, analytics)
// For data fetching, use validateAndFetch which throws ValidationError
const silentTrack = async (fn, context = 'tracking') => {
  try {
    return await fn();
  } catch (error) {
    console.error(`❌ Error in ${context}:`, error);
    console.warn('⚠️ Tracking failed but app continues normally');

    // Log to Firestore but don't break
    await writeRawError(error, {
      componentName: `DataService_${context}`,
      severity: 'warning',
      operationType: 'silent_tracking_failure',
      context
    });

    return null;
  }
};

// ============================================================================
// VALIDATED DATA FETCHING - THROWS ON FAILURE (for ErrorBoundary to catch)
// ============================================================================

/**
 * Validate and return data, throwing ValidationError if invalid
 * @param {any} data - Data to validate
 * @param {Function} validator - Validation function from validationSchemas.js
 * @param {Object} context - Context for error reporting
 * @returns {any} Validated data
 * @throws {ValidationError} If validation fails
 */
const validateData = (data, validator, context = {}) => {
  try {
    const validated = validator(data, context);
    return validated;
  } catch (error) {
    console.error('❌ Data Validation Failed:', error);

    // Log detailed error info
    const errorContext = {
      ...context,
      componentName: 'DataService_Validation',
      operationType: 'validation_failure',
      severity: 'high',
      isValidationError: error instanceof ValidationError || error.isValidationError,
      validationErrors: error.errors || [],
      dataSnapshot: data ? JSON.stringify(data).slice(0, 500) : 'null'
    };

    writeRawError(error, errorContext);

    throw error;
  }
};
const REQUEST_TIMEOUT = 10000; // 10 seconds

// Offline storage configuration
const OFFLINE_STORAGE_VERSION = '1.0';
const OFFLINE_DATA_EXPIRY = 7 * 24 * 60 * 60 * 1000; // 7 days
const OFFLINE_PROGRESS_QUEUE_KEY = 'offline_progress_queue';
// const OFFLINE_CONTENT_CACHE_KEY = 'offline_content_cache'; // Unused
const OFFLINE_SYNC_STATUS_KEY = 'offline_sync_status';

// Enhanced cache with metadata
const cache = new Map();
const pendingRequests = new Map(); // Request deduplication
// const retryQueue = new Map(); // Retry management - Unused
// const offlineSyncListeners = new Set(); // Offline sync event listeners - Unused

// Enhanced error handling
class DataServiceError extends Error {
  constructor(message, code, details = null, retryable = false) {
    super(message);
    this.name = 'DataServiceError';
    this.code = code;
    this.details = details;
    this.retryable = retryable;
    this.timestamp = new Date().toISOString();
  }
}

// Network status detection
const isOnline = () => navigator.onLine;
const isFirebaseAvailable = () => db !== null;

// ============================================================================
// COMPREHENSIVE OFFLINE STORAGE SYSTEM
// ============================================================================

// Offline storage utilities
const OfflineStorage = {
  // Store data in localStorage with metadata
  setItem: (key, data, metadata = {}) => {
    try {
      const storageData = {
        data,
        metadata: {
          version: OFFLINE_STORAGE_VERSION,
          timestamp: Date.now(),
          expiry: Date.now() + OFFLINE_DATA_EXPIRY,
          ...metadata
        }
      };
      localStorage.setItem(key, JSON.stringify(storageData));
      console.log(`📱 Offline storage: Stored ${key}`, { size: JSON.stringify(data).length });
      return true;
    } catch (error) {
      console.error(`❌ Offline storage error for ${key}:`, error);
      return false;
    }
  },

  // Retrieve data from localStorage with validation
  getItem: (key) => {
    try {
      const stored = localStorage.getItem(key);
      if (!stored) return null;

      const parsed = JSON.parse(stored);

      // Check if data is expired
      if (parsed.metadata.expiry && Date.now() > parsed.metadata.expiry) {
        console.warn(`📱 Offline storage: ${key} expired, removing`);
        localStorage.removeItem(key);
        return null;
      }

      console.log(`📱 Offline storage: Retrieved ${key}`, {
        age: Date.now() - parsed.metadata.timestamp,
        version: parsed.metadata.version
      });
      return parsed.data;
    } catch (error) {
      console.error(`❌ Offline storage retrieval error for ${key}:`, error);
      localStorage.removeItem(key); // Remove corrupted data
      return null;
    }
  },

  // Remove item from localStorage
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
      console.log(`📱 Offline storage: Removed ${key}`);
      return true;
    } catch (error) {
      console.error(`❌ Offline storage removal error for ${key}:`, error);
      return false;
    }
  },

  // Get all offline storage keys
  getAllKeys: () => {
    try {
      return Object.keys(localStorage).filter(key =>
        key.startsWith('offline_') ||
        key.startsWith('grammar_') ||
        key.startsWith('vocab_') ||
        key.startsWith('exercise_')
      );
    } catch (error) {
      console.error('❌ Error getting offline storage keys:', error);
      return [];
    }
  },

  // Clear all offline data
  clearAll: () => {
    try {
      const keys = OfflineStorage.getAllKeys();
      keys.forEach(key => localStorage.removeItem(key));
      console.log(`📱 Offline storage: Cleared ${keys.length} items`);
      return keys.length;
    } catch (error) {
      console.error('❌ Error clearing offline storage:', error);
      return 0;
    }
  },

  // Get storage statistics
  getStats: () => {
    try {
      const keys = OfflineStorage.getAllKeys();
      let totalSize = 0;
      const stats = {
        totalItems: keys.length,
        totalSize: 0,
        items: []
      };

      keys.forEach(key => {
        const item = localStorage.getItem(key);
        if (item) {
          const size = item.length;
          totalSize += size;
          stats.items.push({ key, size });
        }
      });

      stats.totalSize = totalSize;
      return stats;
    } catch (error) {
      console.error('❌ Error getting offline storage stats:', error);
      return { totalItems: 0, totalSize: 0, items: [] };
    }
  }
};

// Offline progress queue system
const OfflineProgressQueue = {
  // Add progress to offline queue
  enqueue: (progressData) => {
    try {
      const queue = OfflineProgressQueue.getQueue();
      const queueItem = {
        id: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        data: progressData,
        timestamp: Date.now(),
        attempts: 0,
        maxAttempts: 3
      };

      queue.push(queueItem);
      OfflineStorage.setItem(OFFLINE_PROGRESS_QUEUE_KEY, queue, {
        type: 'progress_queue',
        count: queue.length
      });

      console.log(`📱 Offline queue: Enqueued progress item`, {
        id: queueItem.id,
        queueSize: queue.length
      });

      // Notify listeners
      OfflineProgressQueue.notifyListeners('enqueued', queueItem);

      return queueItem.id;
    } catch (error) {
      console.error('❌ Error enqueuing offline progress:', error);
      return null;
    }
  },

  // Get current queue
  getQueue: () => {
    return OfflineStorage.getItem(OFFLINE_PROGRESS_QUEUE_KEY) || [];
  },

  // Remove item from queue
  dequeue: (itemId) => {
    try {
      const queue = OfflineProgressQueue.getQueue();
      const filteredQueue = queue.filter(item => item.id !== itemId);

      if (filteredQueue.length !== queue.length) {
        OfflineStorage.setItem(OFFLINE_PROGRESS_QUEUE_KEY, filteredQueue, {
          type: 'progress_queue',
          count: filteredQueue.length
        });
        console.log(`📱 Offline queue: Dequeued item ${itemId}`);
        return true;
      }
      return false;
    } catch (error) {
      console.error('❌ Error dequeuing offline progress:', error);
      return false;
    }
  },

  // Clear entire queue
  clearQueue: () => {
    try {
      OfflineStorage.removeItem(OFFLINE_PROGRESS_QUEUE_KEY);
      console.log('📱 Offline queue: Cleared entire queue');
      OfflineProgressQueue.notifyListeners('cleared', null);
      return true;
    } catch (error) {
      console.error('❌ Error clearing offline queue:', error);
      return false;
    }
  },

  // Get queue statistics
  getStats: () => {
    const queue = OfflineProgressQueue.getQueue();
    return {
      totalItems: queue.length,
      oldestItem: queue.length > 0 ? Math.min(...queue.map(item => item.timestamp)) : null,
      newestItem: queue.length > 0 ? Math.max(...queue.map(item => item.timestamp)) : null,
      failedItems: queue.filter(item => item.attempts >= item.maxAttempts).length
    };
  },

  // Event listeners for queue changes
  listeners: new Set(),
  addListener: (callback) => {
    OfflineProgressQueue.listeners.add(callback);
  },
  removeListener: (callback) => {
    OfflineProgressQueue.listeners.delete(callback);
  },
  notifyListeners: (event, data) => {
    OfflineProgressQueue.listeners.forEach(callback => {
      try {
        callback(event, data);
      } catch (error) {
        console.error('❌ Error in offline queue listener:', error);
      }
    });
  }
};

// Offline content caching system
const OfflineContentCache = {
  // Cache content for offline use
  cacheContent: (type, key, content, metadata = {}) => {
    try {
      const cacheKey = `offline_${type}_${key}`;
      const cacheData = {
        content,
        metadata: {
          type,
          originalKey: key,
          cachedAt: Date.now(),
          ...metadata
        }
      };

      OfflineStorage.setItem(cacheKey, cacheData, {
        type: 'content_cache',
        contentType: type
      });

      console.log(`📱 Offline cache: Cached ${type} content`, {
        key,
        size: JSON.stringify(content).length
      });

      return true;
    } catch (error) {
      console.error(`❌ Error caching ${type} content:`, error);
      return false;
    }
  },

  // Retrieve cached content
  getCachedContent: (type, key) => {
    try {
      const cacheKey = `offline_${type}_${key}`;
      const cached = OfflineStorage.getItem(cacheKey);

      if (cached && cached.content) {
        console.log(`📱 Offline cache: Retrieved ${type} content`, {
          key,
          age: Date.now() - cached.metadata.cachedAt
        });
        return cached.content;
      }

      return null;
    } catch (error) {
      console.error(`❌ Error retrieving cached ${type} content:`, error);
      return null;
    }
  },

  // Cache grammar data
  cacheGrammar: (level, chapter, institution, data) => {
    const key = `${institution}_level${level}_chapter${chapter}`;
    return OfflineContentCache.cacheContent('grammar', key, data, {
      level,
      chapter,
      institution
    });
  },

  // Get cached grammar data
  getCachedGrammar: (level, chapter, institution) => {
    const key = `${institution}_level${level}_chapter${chapter}`;
    return OfflineContentCache.getCachedContent('grammar', key);
  },

  // Cache vocabulary data
  cacheVocabulary: (level, chapter, institution, data) => {
    const key = `${institution}_level${level}_chapter${chapter}`;
    return OfflineContentCache.cacheContent('vocabulary', key, data, {
      level,
      chapter,
      institution
    });
  },

  // Get cached vocabulary data
  getCachedVocabulary: (level, chapter, institution) => {
    const key = `${institution}_level${level}_chapter${chapter}`;
    return OfflineContentCache.getCachedContent('vocabulary', key);
  },

  // Cache exercise data
  cacheExercises: (type, level, chapter, institution, data) => {
    const key = `${institution}_level${level}_chapter${chapter}`;
    return OfflineContentCache.cacheContent(`exercise_${type}`, key, data, {
      exerciseType: type,
      level,
      chapter,
      institution
    });
  },

  // Get cached exercise data
  getCachedExercises: (type, level, chapter, institution) => {
    const key = `${institution}_level${level}_chapter${chapter}`;
    return OfflineContentCache.getCachedContent(`exercise_${type}`, key);
  },

  // Cache dialogue data
  cacheDialogue: (level, chapter, institution, data) => {
    const key = `${institution}_level${level}_chapter${chapter}`;
    return OfflineContentCache.cacheContent('dialogue', key, data, {
      level,
      chapter,
      institution
    });
  },

  // Get cached dialogue data
  getCachedDialogue: (level, chapter, institution) => {
    const key = `${institution}_level${level}_chapter${chapter}`;
    return OfflineContentCache.getCachedContent('dialogue', key);
  },

  // Clear all cached content
  clearCache: () => {
    try {
      const keys = OfflineStorage.getAllKeys();
      const cacheKeys = keys.filter(key => key.startsWith('offline_grammar_') ||
        key.startsWith('offline_vocabulary_') ||
        key.startsWith('offline_exercise_') ||
        key.startsWith('offline_dialogue_'));

      cacheKeys.forEach(key => OfflineStorage.removeItem(key));
      console.log(`📱 Offline cache: Cleared ${cacheKeys.length} cached items`);
      return cacheKeys.length;
    } catch (error) {
      console.error('❌ Error clearing offline cache:', error);
      return 0;
    }
  }
};

// Offline sync system
const OfflineSync = {
  // Sync status tracking
  isSyncing: false,
  lastSyncTime: null,
  syncErrors: [],

  // Start sync process
  startSync: async () => {
    if (OfflineSync.isSyncing) {
      console.log('📱 Offline sync: Already syncing, skipping');
      return false;
    }

    if (!isOnline() || !isFirebaseAvailable()) {
      console.log('📱 Offline sync: Not online or Firebase unavailable');
      return false;
    }

    OfflineSync.isSyncing = true;
    OfflineSync.syncErrors = [];

    try {
      console.group('📱 Offline sync: Starting sync process');

      // Sync progress queue
      const syncResult = await OfflineSync.syncProgressQueue();

      // Update sync status
      OfflineSync.lastSyncTime = Date.now();
      OfflineStorage.setItem(OFFLINE_SYNC_STATUS_KEY, {
        lastSyncTime: OfflineSync.lastSyncTime,
        syncErrors: OfflineSync.syncErrors,
        queueSize: OfflineProgressQueue.getStats().totalItems
      });

      console.log('📱 Offline sync: Sync completed', {
        syncedItems: syncResult.syncedCount,
        errors: OfflineSync.syncErrors.length
      });
      console.groupEnd();

      // Notify listeners
      OfflineSync.notifyListeners('completed', syncResult);

      return syncResult;
    } catch (error) {
      console.error('❌ Offline sync error:', error);
      OfflineSync.syncErrors.push({
        timestamp: Date.now(),
        error: error.message,
        type: 'sync_error'
      });

      OfflineSync.notifyListeners('error', error);
      return { success: false, error: error.message };
    } finally {
      OfflineSync.isSyncing = false;
    }
  },

  // Sync progress queue to Firebase
  syncProgressQueue: async () => {
    const queue = OfflineProgressQueue.getQueue();
    let syncedCount = 0;
    let errorCount = 0;

    console.log(`📱 Offline sync: Syncing ${queue.length} queued items`);

    for (const item of queue) {
      try {
        // Increment attempt count
        item.attempts += 1;

        // Try to sync the progress data
        const success = await OfflineSync.syncProgressItem(item.data);

        if (success) {
          // Remove from queue on success
          OfflineProgressQueue.dequeue(item.id);
          syncedCount++;
          console.log(`📱 Offline sync: Synced item ${item.id}`);
        } else {
          errorCount++;
          console.warn(`📱 Offline sync: Failed to sync item ${item.id}`);
        }
      } catch (error) {
        errorCount++;
        console.error(`📱 Offline sync: Error syncing item ${item.id}:`, error);

        // If max attempts reached, remove from queue
        if (item.attempts >= item.maxAttempts) {
          OfflineProgressQueue.dequeue(item.id);
          OfflineSync.syncErrors.push({
            timestamp: Date.now(),
            error: error.message,
            itemId: item.id,
            type: 'max_attempts_reached'
          });
        }
      }
    }

    return { syncedCount, errorCount, totalItems: queue.length };
  },

  // Sync individual progress item
  syncProgressItem: async (progressData) => {
    try {
      // Determine the type of progress data and sync accordingly
      if (progressData.type === 'exercise_attempt') {
        await ProgressService.saveExerciseAttemptWithSession(
          progressData.userId,
          progressData.level,
          progressData.chapter,
          progressData.sessionType,
          progressData.exerciseData
        );
      } else if (progressData.type === 'lesson_mastery') {
        await ProgressService.updateLessonMastery(
          progressData.userId,
          progressData.lessonId,
          progressData.masteryData
        );
      } else if (progressData.type === 'session_progress') {
        await ProgressService.createSessionProgress(
          progressData.userId,
          progressData.level,
          progressData.chapter,
          progressData.sessionType
        );
      }

      return true;
    } catch (error) {
      console.error('❌ Error syncing progress item:', error);
      return false;
    }
  },

  // Event listeners for sync events
  listeners: new Set(),
  addListener: (callback) => {
    OfflineSync.listeners.add(callback);
  },
  removeListener: (callback) => {
    OfflineSync.listeners.delete(callback);
  },
  notifyListeners: (event, data) => {
    OfflineSync.listeners.forEach(callback => {
      try {
        callback(event, data);
      } catch (error) {
        console.error('❌ Error in offline sync listener:', error);
      }
    });
  },

  // Get sync status
  getStatus: () => {
    const stored = OfflineStorage.getItem(OFFLINE_SYNC_STATUS_KEY);
    return {
      isSyncing: OfflineSync.isSyncing,
      lastSyncTime: OfflineSync.lastSyncTime || (stored ? stored.lastSyncTime : null),
      syncErrors: OfflineSync.syncErrors,
      queueSize: OfflineProgressQueue.getStats().totalItems,
      isOnline: isOnline(),
      firebaseAvailable: isFirebaseAvailable()
    };
  }
};

// Offline indicators and user feedback
const OfflineIndicators = {
  // Show offline indicator
  showOfflineIndicator: () => {
    // Create or update offline indicator in DOM
    let indicator = document.getElementById('offline-indicator');
    if (!indicator) {
      indicator = document.createElement('div');
      indicator.id = 'offline-indicator';
      indicator.style.cssText = `
        position: fixed;
        top: 10px;
        right: 10px;
        background: #ff6b6b;
        color: white;
        padding: 8px 16px;
        border-radius: 4px;
        font-size: 14px;
        font-weight: bold;
        z-index: 10000;
        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        display: flex;
        align-items: center;
        gap: 8px;
      `;
      document.body.appendChild(indicator);
    }

    indicator.innerHTML = `
      <span>📱</span>
      <span>Offline Mode</span>
    `;
    indicator.style.display = 'flex';
  },

  // Hide offline indicator
  hideOfflineIndicator: () => {
    const indicator = document.getElementById('offline-indicator');
    if (indicator) {
      indicator.style.display = 'none';
    }
  },

  // Show sync indicator
  showSyncIndicator: (message = 'Syncing...') => {
    let indicator = document.getElementById('sync-indicator');
    if (!indicator) {
      indicator = document.createElement('div');
      indicator.id = 'sync-indicator';
      indicator.style.cssText = `
        position: fixed;
        top: 50px;
        right: 10px;
        background: #4ecdc4;
        color: white;
        padding: 8px 16px;
        border-radius: 4px;
        font-size: 14px;
        font-weight: bold;
        z-index: 10000;
        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        display: flex;
        align-items: center;
        gap: 8px;
      `;
      document.body.appendChild(indicator);
    }

    indicator.innerHTML = `
      <span>🔄</span>
      <span>${message}</span>
    `;
    indicator.style.display = 'flex';
  },

  // Hide sync indicator
  hideSyncIndicator: () => {
    const indicator = document.getElementById('sync-indicator');
    if (indicator) {
      indicator.style.display = 'none';
    }
  },

  // Show sync success message
  showSyncSuccess: (syncedCount) => {
    const indicator = document.getElementById('sync-indicator');
    if (indicator) {
      indicator.innerHTML = `
        <span>✅</span>
        <span>Synced ${syncedCount} items</span>
      `;
      indicator.style.background = '#51cf66';

      setTimeout(() => {
        OfflineIndicators.hideSyncIndicator();
      }, 3000);
    }
  },

  // Show sync error message
  showSyncError: (errorMessage) => {
    const indicator = document.getElementById('sync-indicator');
    if (indicator) {
      indicator.innerHTML = `
        <span>❌</span>
        <span>Sync failed: ${errorMessage}</span>
      `;
      indicator.style.background = '#ff6b6b';

      setTimeout(() => {
        OfflineIndicators.hideSyncIndicator();
      }, 5000);
    }
  }
};

// Initialize offline system
const initializeOfflineSystem = () => {
  // Listen for online/offline events
  window.addEventListener('online', () => {
    console.log('📱 Network: Back online, starting sync');
    OfflineIndicators.hideOfflineIndicator();
    OfflineSync.startSync();
  });

  window.addEventListener('offline', () => {
    console.log('📱 Network: Gone offline');
    OfflineIndicators.showOfflineIndicator();
  });

  // Auto-sync when coming back online
  if (isOnline() && isFirebaseAvailable()) {
    const queueStats = OfflineProgressQueue.getStats();
    if (queueStats.totalItems > 0) {
      console.log(`📱 Auto-sync: Found ${queueStats.totalItems} queued items`);
      OfflineSync.startSync();
    }
  } else {
    OfflineIndicators.showOfflineIndicator();
  }

  // Set up sync listeners
  OfflineSync.addListener((event, data) => {
    switch (event) {
      case 'completed':
        OfflineIndicators.showSyncSuccess(data.syncedCount);
        break;
      case 'error':
        OfflineIndicators.showSyncError(data.message);
        break;
      default:
        console.log('Unknown offline sync event:', event);
        break;
    }
  });

  console.log('📱 Offline system: Initialized');
};

// Initialize offline system when module loads (only in browser)
if (typeof window !== 'undefined') {
  initializeOfflineSystem();
}

// Request deduplication
const getRequestKey = (type, ...params) => `${type}_${params.join('_')}`;

const getPendingRequest = (key) => pendingRequests.get(key);
const setPendingRequest = (key, promise) => {
  pendingRequests.set(key, promise);
  // Clean up after request completes
  promise.finally(() => {
    pendingRequests.delete(key);
  });
};

// Cache management
const getCacheKey = (type, ...params) => `${type}_${params.join('_')}`;

const getCachedData = (key) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    cached.hitCount = (cached.hitCount || 0) + 1;
    return cached.data;
  }
  return null;
};

const setCachedData = (key, data, metadata = {}) => {
  cache.set(key, {
    data,
    timestamp: Date.now(),
    hitCount: 0,
    metadata: {
      source: 'firebase',
      ...metadata
    }
  });
};

const clearCache = (pattern = null) => {
  if (pattern) {
    for (const key of cache.keys()) {
      if (key.includes(pattern)) {
        cache.delete(key);
      }
    }
  } else {
    cache.clear();
  }
};

// Retry logic with exponential backoff
const retryWithBackoff = async (fn, attempts = RETRY_ATTEMPTS, delay = RETRY_DELAY) => {
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (error) {
      if (i === attempts - 1) throw error;

      // Only retry if error is retryable
      if (error.retryable !== false) {
        const currentDelay = delay;
        console.warn(`🔄 Retry attempt ${i + 1}/${attempts} after ${currentDelay}ms`);
        await new Promise(resolve => setTimeout(resolve, currentDelay));
        delay *= 2; // Exponential backoff
      } else {
        throw error;
      }
    }
  }
};

// Timeout wrapper
const withTimeout = (promise, timeout = REQUEST_TIMEOUT) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new DataServiceError('Request timeout', 'TIMEOUT', null, true)), timeout)
    )
  ]);
};

// Enhanced data fetching with comprehensive error handling
const fetchFromFirestore = async (collectionPath, options = {}) => {
  const requestKey = getRequestKey('firestore', collectionPath, JSON.stringify(options));

  // Check for pending request (deduplication)
  const pendingRequest = getPendingRequest(requestKey);
  if (pendingRequest) {
    console.log(`🔄 Deduplicating request for ${collectionPath}`);
    return pendingRequest;
  }

  // Check cache first
  const cacheKey = getCacheKey('firestore', collectionPath, JSON.stringify(options));
  const cached = getCachedData(cacheKey);
  if (cached) {
    console.log(`📦 Cache hit for ${collectionPath}`);
    return cached;
  }

  // Check network status
  if (!isOnline()) {
    throw new DataServiceError('No internet connection', 'OFFLINE', { collectionPath }, true);
  }

  if (!isFirebaseAvailable()) {
    throw new DataServiceError('Firebase not available', 'FIREBASE_UNAVAILABLE', { collectionPath }, true);
  }

  console.log(`🔍 Attempting fetch from: ${collectionPath}`);

  const fetchPromise = retryWithBackoff(async () => {
    try {
      pushLog(`🔥 Fetching from ${collectionPath}`, 'info', 'data');
      console.group(`🔥 DataService: Fetching from ${collectionPath}`);
      console.log('Options:', options);

      let q = collection(db, collectionPath);

      if (options.where) {
        q = query(q, where(options.where.field, options.where.operator, options.where.value));
      }

      if (options.orderBy) {
        q = query(q, orderBy(options.orderBy.field, options.orderBy.direction || 'asc'));
      }

      if (options.limit) {
        q = query(q, limit(options.limit));
      }

      const snapshot = await withTimeout(getDocs(q));
      const data = [];

      snapshot.forEach((doc) => {
        data.push({
          id: doc.id,
          ...doc.data()
        });
      });

      pushLog(`✅ Fetched ${data.length} docs from ${collectionPath}`, 'success', 'data');
      console.log(`✅ Fetched ${data.length} documents`);
      console.groupEnd();

      // Cache the result
      setCachedData(cacheKey, data, {
        source: 'firebase',
        collectionPath,
        options,
        documentCount: data.length
      });

      return data;
    } catch (error) {
      writeRawError(error, {
        componentName: 'DataService_Firestore',
        collectionPath,
        options,
        attemptedPath: collectionPath,
        errorCode: error.code,
        errorMessage: error.message
      });
      pushLog(`❌ Fetch FAILED: ${collectionPath} - ${error.message}`, 'error', 'data');
      console.error(`❌ Error fetching from ${collectionPath}:`, error);

      // Determine if error is retryable
      const retryable = error.code === 'unavailable' ||
        error.code === 'deadline-exceeded' ||
        error.code === 'resource-exhausted' ||
        error.message.includes('timeout');

      throw new DataServiceError(
        `Failed to fetch data from ${collectionPath}`,
        'FETCH_ERROR',
        { collectionPath, options, error: error.message },
        retryable
      );
    }
  });

  // Store pending request for deduplication
  setPendingRequest(requestKey, fetchPromise);

  return fetchPromise;
};

// Data validation and transformation
const validateAndTransformGrammarData = (data) => {
  if (!Array.isArray(data)) return [];

  return data.map(item => ({
    id: item.id || 'unknown',
    title_kr: item.title_kr || item.titleKR || item.grammarPoint || 'Grammar Point',
    title_en: item.title_en || item.titleEN || item.usage || 'Usage',
    explanation: item.explanation || item.brief_notes || item.usage || 'Explanation not available',
    regular_examples: item.regular_examples || item.examples || item.step1?.examples || [],
    conversation_examples: item.conversation_examples || item.conversationExamples || [],
    irregular_examples: item.irregular_examples || item.irregularExamples || [],
    forms: item.forms || [],
    rules: item.rules || [],
    nuance: item.nuance || '',
    register: item.register || 'beginner',
    grammar_group: item.grammar_group || item.category || 'Category',
    chapter: item.chapter || 'Chapter',
    // Keep original data for debugging
    _original: item
  }));
};

// Enhanced offline data fallback with comprehensive caching
const getOfflineFallback = (type, ...params) => {
  console.warn(`📱 Using offline fallback for ${type}`);

  switch (type) {
    case 'grammar_points':
      const [level, chapter, institution] = params;

      // First try cached offline data
      const cachedGrammar = OfflineContentCache.getCachedGrammar(level, chapter, institution);
      if (cachedGrammar) {
        console.log(`📱 Using cached offline grammar for Level ${level} Chapter ${chapter}`);
        return { ...cachedGrammar, source: 'offline_cached' };
      }

      // Fallback to schema for Level 2
      if (level === 2) {
        const schemaData = GrammarSchemaUtils.getGrammarPointsForChapter(chapter);
        const result = {
          learning: validateAndTransformGrammarData(schemaData),
          exercises: [],
          total: schemaData.length,
          source: 'offline_schema_fallback'
        };

        // Cache the schema data for future offline use
        OfflineContentCache.cacheGrammar(level, chapter, institution, result);

        return result;
      }
      break;

    case 'comprehensive_grammar':
      const [level2] = params;
      if (level2 === 2) {
        return {
          level: LEVEL_2_GRAMMAR_SCHEMA.level,
          title: LEVEL_2_GRAMMAR_SCHEMA.title,
          description: LEVEL_2_GRAMMAR_SCHEMA.description,
          totalChapters: LEVEL_2_GRAMMAR_SCHEMA.totalChapters,
          totalGrammarPoints: LEVEL_2_GRAMMAR_SCHEMA.totalGrammarPoints,
          chapters: LEVEL_2_GRAMMAR_SCHEMA.chapters,
          source: 'offline_schema_fallback'
        };
      }
      break;

    case 'vocabulary':
      const [level3, chapter3, institution3] = params;

      // Try cached offline vocabulary
      const cachedVocab = OfflineContentCache.getCachedVocabulary(level3, chapter3, institution3);
      if (cachedVocab) {
        console.log(`📱 Using cached offline vocabulary for Level ${level3} Chapter ${chapter3}`);
        return { ...cachedVocab, source: 'offline_cached' };
      }

      // Return empty array for offline vocabulary (no schema fallback)
      return { data: [], source: 'offline_empty' };

    case 'exercises':
      const [exerciseType, level4, chapter4, institution4] = params;

      // Try cached offline exercises
      const cachedExercises = OfflineContentCache.getCachedExercises(exerciseType, level4, chapter4, institution4);
      if (cachedExercises) {
        console.log(`📱 Using cached offline ${exerciseType} exercises for Level ${level4} Chapter ${chapter4}`);
        return { ...cachedExercises, source: 'offline_cached' };
      }

      // Return empty array for offline exercises
      return { data: [], source: 'offline_empty' };

    case 'dialogue_scenes':
      const [level5, chapter5, institution5] = params;

      // Try cached offline dialogue
      const cachedDialogue = OfflineContentCache.getCachedDialogue(level5, chapter5, institution5);
      if (cachedDialogue) {
        console.log(`📱 Using cached offline dialogue for Level ${level5} Chapter ${chapter5}`);
        return { ...cachedDialogue, source: 'offline_cached' };
      }

      // Return empty array for offline dialogue (no schema fallback)
      return { scenes: [], total: 0, source: 'offline_empty' };

    default:
      console.warn(`Unknown offline fallback type: ${type}`);
      return null;
  }

  return null;
};

// Grammar Data Service
export const GrammarService = {
  // Fetch all grammar points for a level and chapter with comprehensive offline support
  async getGrammarPoints(level, chapter, institution = 'Korea_University') {
    const cacheKey = getCacheKey('grammar_points', level, chapter, institution);
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    try {
      // ✅ FIXED: Use Level{level} (PascalCase) not level_{level} (snake_case) for content data
      const collectionPath = `feature_testing/${institution}/Level${level}/Grammar/Chapter${chapter}`;
      const allDocuments = await fetchFromFirestore(collectionPath);

      if (allDocuments.length > 0) {
        // Filter out exercise documents (those ending with _ex)
        const learningData = allDocuments.filter(doc => !doc.id.endsWith('_ex'));
        const exerciseData = allDocuments.filter(doc => doc.id.endsWith('_ex'));

        // Validate and transform the data
        const validatedLearningData = validateAndTransformGrammarData(learningData);

        const result = {
          learning: validatedLearningData,
          exercises: exerciseData,
          total: validatedLearningData.length,
          source: 'firebase'
        };

        // ENHANCED v1.4: Validate result structure before returning
        validateData(result, validateGrammarPointsResult, {
          operation: 'getGrammarPoints',
          firestorePath: collectionPath,
          level,
          chapter,
          institution
        });

        // Cache in both memory and offline storage
        setCachedData(cacheKey, result, {
          source: 'firebase',
          level,
          chapter,
          institution,
          documentCount: allDocuments.length
        });

        // Also cache for offline use
        OfflineContentCache.cacheGrammar(level, chapter, institution, result);

        return result;
      }
    } catch (error) {
      console.warn(`No Firebase data found for Level ${level} Chapter ${chapter}:`, error);

      // Check if we should use offline fallback
      if (error.code === 'OFFLINE' || error.code === 'FIREBASE_UNAVAILABLE' || !isOnline()) {
        const offlineFallback = getOfflineFallback('grammar_points', level, chapter, institution);
        if (offlineFallback) {
          setCachedData(cacheKey, offlineFallback, {
            source: 'offline',
            level,
            chapter,
            institution
          });
          return offlineFallback;
        }
      }
    }

    // Fallback to schema for Level 2
    if (level === 2) {
      const schemaData = GrammarSchemaUtils.getGrammarPointsForChapter(chapter);

      // Generate exercise data from schema
      const exerciseData = schemaData.flatMap(grammarPoint =>
        GrammarSchemaUtils.generateExercisesFromGrammar(grammarPoint, 'cloze')
      );

      // Validate and transform schema data
      const validatedSchemaData = validateAndTransformGrammarData(schemaData);

      const result = {
        learning: validatedSchemaData,
        exercises: exerciseData,
        total: validatedSchemaData.length,
        source: 'schema_fallback'
      };

      // Cache in both memory and offline storage
      setCachedData(cacheKey, result, {
        source: 'schema',
        level,
        chapter,
        institution
      });

      OfflineContentCache.cacheGrammar(level, chapter, institution, result);

      return result;
    }

    // For other levels without data, return empty result
    const result = {
      learning: [],
      exercises: [],
      total: 0,
      source: 'no_data'
    };

    setCachedData(cacheKey, result, {
      source: 'empty',
      level,
      chapter,
      institution
    });
    return result;
  },

  // Fetch specific grammar point
  async getGrammarPoint(level, chapter, grammarId, institution = 'Korea_University') {
    const cacheKey = getCacheKey('grammar_point', level, chapter, grammarId, institution);
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    try {
      // ✅ FIXED: Use Level{level} (PascalCase) not level_{level} (snake_case) for content data
      const docPath = `feature_testing/${institution}/Level${level}/Grammar/Chapter${chapter}/${grammarId}`;
      console.log(`🔍 Fetching grammar point from: ${docPath}`);
      const docRef = doc(db, docPath);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const result = {
          id: docSnap.id,
          ...docSnap.data(),
          source: 'firebase'
        };

        setCachedData(cacheKey, result);
        return result;
      }
    } catch (error) {
      await writeRawError(error, {
        componentName: 'GrammarService',
        level,
        chapter,
        grammarId,
        operation: 'getGrammarPoint',
        attemptedPath: `feature_testing/${institution}/Level${level}/Grammar/Chapter${chapter}/${grammarId}`
      });
      console.warn(`No Firebase data found for grammar point ${grammarId}:`, error);
    }

    // Fallback to schema for Level 2
    if (level === 2) {
      const schemaGrammarPoint = GrammarSchemaUtils.getGrammarPointById(grammarId);
      if (schemaGrammarPoint) {
        const result = {
          ...schemaGrammarPoint,
          source: 'schema_fallback'
        };

        setCachedData(cacheKey, result);
        return result;
      }
    }

    // If not found anywhere
    throw new DataServiceError(
      `Grammar point ${grammarId} not found`,
      'NOT_FOUND',
      { level, chapter, grammarId }
    );
  },

  // Get available grammar chapters for a level
  async getAvailableChapters(level, institution = 'Korea_University') {
    const cacheKey = getCacheKey('grammar_chapters', level, institution);
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    // For Level 2, use schema data
    if (level === 2) {
      const chapters = Object.keys(LEVEL_2_GRAMMAR_SCHEMA.chapters).map(Number).sort((a, b) => a - b);
      setCachedData(cacheKey, chapters);
      return chapters;
    }

    // For other levels, try Firebase but fallback gracefully
    try {
      // Try to fetch from the correct collection structure - this path is invalid for Firestore
      // Firestore collections must have odd number of segments, but this has 4
      // We'll skip this and go straight to fallback
      console.warn(`Skipping Firebase fetch for level ${level} - invalid collection path structure`);
      throw new Error('Invalid collection path - skipping Firebase fetch');
    } catch (error) {
      console.warn(`Could not fetch grammar chapters for level ${level}:`, error.message);
    }

    // Fallback to default chapters if Firebase fails
    const defaultChapters = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
    setCachedData(cacheKey, defaultChapters);
    return defaultChapters;
  },

  // Get comprehensive grammar information for a level
  async getComprehensiveGrammar(level, institution = 'Korea_University') {
    const cacheKey = getCacheKey('comprehensive_grammar', level, institution);
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    if (level === 2) {
      const result = {
        level: LEVEL_2_GRAMMAR_SCHEMA.level,
        title: LEVEL_2_GRAMMAR_SCHEMA.title,
        description: LEVEL_2_GRAMMAR_SCHEMA.description,
        totalChapters: LEVEL_2_GRAMMAR_SCHEMA.totalChapters,
        totalGrammarPoints: LEVEL_2_GRAMMAR_SCHEMA.totalGrammarPoints,
        chapters: LEVEL_2_GRAMMAR_SCHEMA.chapters,
        source: 'schema'
      };

      setCachedData(cacheKey, result);
      return result;
    }

    // For other levels, return basic structure
    const chapters = await this.getAvailableChapters(level, institution);
    const result = {
      level: level,
      title: `Level ${level} Grammar`,
      description: `Grammar patterns for Level ${level}`,
      totalChapters: chapters.length,
      totalGrammarPoints: 0, // Would need to count from Firebase
      chapters: {},
      source: 'firebase'
    };

    setCachedData(cacheKey, result);
    return result;
  },

  // Fetch exercise data for a specific grammar point
  async getGrammarExercises(level, chapter, grammarId, institution = 'Korea_University') {
    const cacheKey = getCacheKey('grammar_exercises', level, chapter, grammarId, institution);
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    try {
      // ✅ FIXED: Use Level{level} (PascalCase) not level_{level} (snake_case) for content data
      const exerciseId = `${grammarId}_ex`;
      const docPath = `feature_testing/${institution}/Level${level}/Grammar/Chapter${chapter}/${exerciseId}`;
      const docRef = doc(db, docPath);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const exerciseData = {
          id: docSnap.id,
          ...docSnap.data(),
          source: 'firebase'
        };

        setCachedData(cacheKey, exerciseData);
        return exerciseData;
      }
    } catch (error) {
      console.warn(`No exercise data found for grammar point ${grammarId}:`, error);
    }

    // Fallback to schema-generated exercises for Level 2
    if (level === 2) {
      const grammarPoint = GrammarSchemaUtils.getGrammarPointById(grammarId);
      if (grammarPoint) {
        const exerciseData = {
          id: `${grammarId}_ex`,
          learning_tracking: GrammarSchemaUtils.generateExercisesFromGrammar(grammarPoint, 'cloze'),
          source: 'schema_fallback'
        };

        setCachedData(cacheKey, exerciseData);
        return exerciseData;
      }
    }

    // Return empty exercise data if not found
    const result = {
      id: `${grammarId}_ex`,
      learning_tracking: {},
      source: 'no_data'
    };

    setCachedData(cacheKey, result);
    return result;
  }
};

// Dialogue Data Service
export const DialogueService = {
  // Fetch all dialogue scenes for a level and chapter
  async getDialogueScenes(level, chapter, institution = 'Korea_University') {
    const cacheKey = getCacheKey('dialogue_scenes', level, chapter, institution);
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    try {
      const collectionPath = `feature_testing/${institution}/Level${level}/Dialogue/Chapter${chapter}`;
      console.log(`🎭 Fetching dialogue scenes from: ${collectionPath}`);
      const allDocuments = await fetchFromFirestore(collectionPath);

      if (allDocuments.length > 0) {
        // Validate dialogue data structure
        const validatedScenes = validateData(allDocuments, validateDialogueArray, {
          operation: 'getDialogueScenes',
          firestorePath: collectionPath,
          level,
          chapter,
          institution
        });

        const result = {
          scenes: validatedScenes,
          total: validatedScenes.length,
          source: 'firebase'
        };

        // Cache in both memory and offline storage
        setCachedData(cacheKey, result, {
          source: 'firebase',
          level,
          chapter,
          institution,
          documentCount: allDocuments.length
        });

        OfflineContentCache.cacheDialogue(level, chapter, institution, result);

        return result;
      }
    } catch (error) {
      await writeRawError(error, {
        componentName: 'DialogueService',
        level,
        chapter,
        operation: 'getDialogueScenes',
        attemptedPath: `feature_testing/${institution}/Level${level}/Dialogue/Chapter${chapter}`
      });
      console.warn(`🎭 No Firebase dialogue data found for Level ${level} Chapter ${chapter}:`, error);

      // Check if we should use offline fallback
      if (error.code === 'OFFLINE' || error.code === 'FIREBASE_UNAVAILABLE' || !isOnline()) {
        const offlineFallback = getOfflineFallback('dialogue_scenes', level, chapter, institution);
        if (offlineFallback) {
          setCachedData(cacheKey, offlineFallback, {
            source: 'offline',
            level,
            chapter,
            institution
          });
          return offlineFallback;
        }
      }
    }

    // Return empty result when no data found anywhere
    const result = {
      scenes: [],
      total: 0,
      source: 'no_data'
    };

    setCachedData(cacheKey, result, {
      source: 'empty',
      level,
      chapter,
      institution
    });
    return result;
  },

  // Fetch a single dialogue scene by document ID
  async getDialogueScene(level, chapter, sceneId, institution = 'Korea_University') {
    const cacheKey = getCacheKey('dialogue_scene', level, chapter, sceneId, institution);
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    try {
      const docPath = `feature_testing/${institution}/Level${level}/Dialogue/Chapter${chapter}/${sceneId}`;
      console.log(`🎭 Fetching dialogue scene from: ${docPath}`);
      const docRef = doc(db, docPath);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const result = {
          id: docSnap.id,
          ...docSnap.data(),
          source: 'firebase'
        };

        setCachedData(cacheKey, result);
        return result;
      }
    } catch (error) {
      await writeRawError(error, {
        componentName: 'DialogueService',
        level,
        chapter,
        sceneId,
        operation: 'getDialogueScene',
        attemptedPath: `feature_testing/${institution}/Level${level}/Dialogue/Chapter${chapter}/${sceneId}`
      });
      console.warn(`🎭 No dialogue scene found for ${sceneId}:`, error);
    }

    throw new DataServiceError(
      `Dialogue scene ${sceneId} not found`,
      'NOT_FOUND',
      { level, chapter, sceneId }
    );
  }
};

// Vocabulary Data Service
export const VocabularyService = {
  // Fetch all vocabulary for a level and chapter
  async getVocabulary(level, chapter, institution = 'Korea_University') {
    const cacheKey = getCacheKey('vocabulary', level, chapter, institution);
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    // ✅ FIXED: Use Level{level} (PascalCase) not level_{level} (snake_case) for content data
    const collectionPath = `feature_testing/${institution}/Level${level}/Vocab/Chapter${chapter}`;
    const data = await fetchFromFirestore(collectionPath);

    // ENHANCED v1.4: Validate vocabulary array structure
    const validatedData = validateData(data, validateVocabularyArray, {
      operation: 'getVocabulary',
      firestorePath: collectionPath,
      level,
      chapter,
      institution
    });

    setCachedData(cacheKey, validatedData);
    return validatedData;
  },

  // Fetch specific vocabulary word
  async getVocabularyWord(level, chapter, wordId, institution = 'Korea_University') {
    const cacheKey = getCacheKey('vocabulary_word', level, chapter, wordId, institution);
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    try {
      // ✅ FIXED: Use Level{level} (PascalCase) not level_{level} (snake_case) for content data
      const docPath = `feature_testing/${institution}/Level${level}/Vocab/Chapter${chapter}/${wordId}`;
      console.log(`🔍 Fetching vocabulary word from: ${docPath}`);
      const docRef = doc(db, docPath);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        const notFoundError = new Error(`Vocabulary word ${wordId} not found`);
        await writeRawError(notFoundError, {
          componentName: 'VocabularyService',
          level,
          chapter,
          wordId,
          operation: 'getVocabularyWord',
          attemptedPath: docPath
        });
        throw new DataServiceError(
          `Vocabulary word ${wordId} not found`,
          'NOT_FOUND',
          { level, chapter, wordId }
        );
      }

      const result = {
        id: docSnap.id,
        ...docSnap.data()
      };

      setCachedData(cacheKey, result);
      return result;
    } catch (error) {
      if (!(error instanceof DataServiceError)) {
        await writeRawError(error, {
          componentName: 'VocabularyService',
          level,
          chapter,
          wordId,
          operation: 'getVocabularyWord',
          attemptedPath: `feature_testing/${institution}/Level${level}/Vocab/Chapter${chapter}/${wordId}`
        });
      }
      throw error;
    }
  },

  // Get available vocabulary chapters for a level
  async getAvailableChapters(level, institution = 'Korea_University') {
    const cacheKey = getCacheKey('vocabulary_chapters', level, institution);
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    // Try to fetch from Firebase but fallback gracefully
    try {
      // Try to fetch from the correct collection structure - this path is invalid for Firestore
      // Firestore collections must have odd number of segments, but this has 4
      // We'll skip this and go straight to fallback
      console.warn(`Skipping Firebase fetch for level ${level} - invalid collection path structure`);
      throw new Error('Invalid collection path - skipping Firebase fetch');
    } catch (error) {
      console.warn(`Could not fetch vocabulary chapters for level ${level}:`, error.message);
    }

    // Fallback to default chapters if Firebase fails
    const defaultChapters = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
    setCachedData(cacheKey, defaultChapters);
    return defaultChapters;
  },

  // Search vocabulary across all levels and chapters
  async searchVocabulary(searchTerm, institution = 'Korea_University') {
    const cacheKey = getCacheKey('vocabulary_search', searchTerm, institution);
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    const results = [];

    // Search across all levels (1-6)
    for (let level = 1; level <= 6; level++) {
      try {
        // ✅ FIXED: Use Level{level} (PascalCase) not level_{level} (snake_case) for content data
        const collectionPath = `feature_testing/${institution}/Level${level}/Vocab`;
        const data = await fetchFromFirestore(collectionPath);

        const filtered = data.filter(doc =>
          doc.korean?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          doc.translations?.english?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          doc.translations?.spanish?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          doc.translations?.japanese?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          doc.translations?.portuguese?.toLowerCase().includes(searchTerm.toLowerCase())
        );

        results.push(...filtered.map(item => ({ ...item, level })));
      } catch (error) {
        console.warn(`Failed to search level ${level}:`, error);
      }
    }

    setCachedData(cacheKey, results);
    return results;
  }
};

// Exercise Data Service
export const ExerciseService = {
  // Generate cloze exercises from grammar data with user access filtering
  // Generate cloze exercises from grammar data with user access filtering
  async generateClozeExercises(level, chapter, institution = 'Korea_University', userId = null) {
    const cacheKey = getCacheKey('cloze_exercises', level, chapter, institution, userId);
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    let grammarData = null;
    try {
      grammarData = await GrammarService.getGrammarPoints(level, chapter, institution);
    } catch (error) {
      console.warn('Error fetching grammar points for cloze exercises:', error);
      // Fallback or empty result will be handled below
    }

    const exercises = [];

    if (!grammarData) {
      console.warn(`No grammar data available for Level ${level} Chapter ${chapter}, returning empty cloze exercises.`);
      setCachedData(cacheKey, []);
      return [];
    }

    // Get user's grammar access status if userId provided
    let userGrammarGenome = {};
    if (userId) {
      try {
        userGrammarGenome = await UserGrammarTracking.getUserGrammarGenome(userId, level, chapter, 'Learn');
        console.log('🧬 User grammar genome:', userGrammarGenome);
      } catch (error) {
        console.warn('Could not get user grammar genome:', error);
      }
    }

    // Use rich exercise data from Firestore if available
    if (grammarData.source === 'firebase' && grammarData.exercises && Array.isArray(grammarData.exercises)) {
      // Extract exercises from the NEW fixed structure (ch1_gmr_001_ex_fixed.json)
      grammarData.exercises.forEach(exerciseDoc => {
        if (exerciseDoc && exerciseDoc.exercises && Array.isArray(exerciseDoc.exercises)) {
          // Transform NEW structure to component-compatible format
          const transformedExercises = exerciseDoc.exercises.map(ex => ({
            id: ex.id,
            type: ex.exercise_type || 'fill_blank',
            question_type: ex.question_type || 'conjugation',
            difficulty: ex.tags?.difficulty || 'simple',
            category: 'Grammar Practice',
            subcategory: ex.question_type || 'General',
            instruction: ex.instruction || 'Complete the exercise',
            question: ex.question || 'Complete the sentence',
            answer: ex.answer || '',
            accepted_answers: ex.accepted_answers || [ex.answer],
            options: ex.options || [],
            grammar_focus: ex.tags?.grammar_focus || 'grammar_practice',
            expected_input: ex.question_type || 'full_verb_progressive',
            validation_rules: {
              accepted_answers: ex.accepted_answers || [ex.answer],
              key_components: ex.tags?.complexity || ['basic_conjugation']
            },
            adaptive_properties: {
              base_verb: ex.base_verb || '먹다',
              tested_component: ex.question_type || 'full_construction',
              speech_level: ex.tags?.speech_level?.[0] || 'polite',
              tense: ex.tags?.tense?.[0] || 'present',
              complexity_factors: ex.tags?.complexity || ['basic_conjugation', 'polite_speech']
            },
            // Add grammar point access requirement
            required_grammar_point: ex.required_grammar_point || this.extractGrammarPointFromExerciseId(ex.id),
            // Add tags for filtering
            tags: ex.tags || {}
          }));
          exercises.push(...transformedExercises);
        }
      });
    } else if (level === 2 && (grammarData.source === 'schema_fallback' || grammarData.source === 'schema')) {
      // Use the pre-generated exercises from schema if available
      if (grammarData.exercises && Array.isArray(grammarData.exercises)) {
        exercises.push(...grammarData.exercises);
      }
    } else {
      // For other levels or Firebase-only data, transform grammar data into cloze exercise format
      if (grammarData.learning && Array.isArray(grammarData.learning)) {
        grammarData.learning.forEach((grammar) => {
          // CRITICAL FIX: Check for regular_examples at BOTH root level AND step1 level
          // Firestore data has regular_examples at root, but some schemas have it in step1
          const regularExamples = grammar.regular_examples || grammar.step1?.regular_examples || [];
          const step1Examples = grammar.step1?.examples || [];
          const allExamples = [...(Array.isArray(regularExamples) ? regularExamples : []), ...(Array.isArray(step1Examples) ? step1Examples : [])];

          console.log(`[CLOZE] Grammar ${grammar.id}: Found ${allExamples.length} examples (regular: ${regularExamples.length || 0}, step1: ${step1Examples.length || 0})`);

          if (allExamples.length > 0) {
            allExamples.forEach((example, exIndex) => {
              // Handle both object examples and string examples
              const exampleText = typeof example === 'string' ? example : (example.sentence_kr || example.result || example.text || '');
              const translation = typeof example === 'object' ? (example.translation || '') : '';

              if (exampleText) {
                exercises.push({
                  id: `ex_${grammar.id}_${exIndex}`,
                  type: 'fill_blank',
                  question_type: 'conjugation',
                  difficulty: this.determineDifficulty(grammar),
                  category: 'Grammar Practice',
                  subcategory: grammar.title_kr || grammar.step1?.explanation || 'Grammar Exercise',
                  instruction: 'Complete the sentence by conjugating the verb',
                  question: this.createClozeQuestion(exampleText),
                  answer: this.extractAnswer(exampleText),
                  accepted_answers: [this.extractAnswer(exampleText)],
                  grammar_focus: grammar.explanation?.core_meaning || grammar.title_en || 'grammar_practice',
                  expected_input: 'full_verb_progressive',
                  translation: translation,
                  validation_rules: {
                    accepted_answers: [this.extractAnswer(exampleText)],
                    key_components: ['고', '있']
                  },
                  adaptive_properties: {
                    base_verb: typeof example === 'object' ? (example.base || '먹다') : '먹다',
                    tested_component: 'full_construction',
                    speech_level: 'polite',
                    tense: 'present',
                    complexity_factors: ['basic_conjugation', 'polite_speech']
                  },
                  required_grammar_point: grammar.id,
                  tags: {
                    difficulty: this.determineDifficulty(grammar),
                    complexity: ['basic_conjugation', 'polite_speech'],
                    focus: ['progressive_construction'],
                    exercise_type: 'fill_blank',
                    tense: ['present'],
                    speech_level: ['polite']
                  }
                });
              }
            });
          }
        });
      }
    }

    // TEMPORARILY DISABLED: Filter exercises based on user's grammar access
    // TODO: Re-enable when all grammar points are available
    let filteredExercises = exercises;
    /*
    if (userId && Object.keys(userGrammarGenome).length > 0) {
      filteredExercises = exercises.filter(exercise => {
        const requiredGrammar = exercise.required_grammar_point;
        if (!requiredGrammar) return true; // No requirement = always available
        
        const hasAccess = userGrammarGenome[requiredGrammar] === true;
        if (!hasAccess) {
          console.log(`🚫 Exercise ${exercise.id} filtered out - user hasn't accessed grammar point ${requiredGrammar}`);
        }
        return hasAccess;
      });
    }
    */

    setCachedData(cacheKey, filteredExercises);
    return filteredExercises;
  },

  // Generate shape puzzle exercises from grammar data
  async generateShapePuzzleExercises(level, chapter, institution = 'Korea_University') {
    const cacheKey = getCacheKey('shape_puzzle_exercises', level, chapter, institution);
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    let grammarData = null;
    try {
      grammarData = await GrammarService.getGrammarPoints(level, chapter, institution);
    } catch (error) {
      console.warn('Error fetching grammar points for shape puzzle:', error);
    }

    const exercises = [];

    if (!grammarData) {
      console.warn(`No grammar data available for Level ${level} Chapter ${chapter}, returning empty shape puzzle exercises.`);
      setCachedData(cacheKey, []);
      return [];
    }

    // Use rich exercise data from Firestore if available (NEW structure)
    if (grammarData.source === 'firebase' && grammarData.exercises && Array.isArray(grammarData.exercises)) {
      // Extract exercises from the NEW fixed structure for shape puzzle
      grammarData.exercises.forEach(exerciseDoc => {
        if (exerciseDoc && exerciseDoc.exercises && Array.isArray(exerciseDoc.exercises)) {
          // Transform NEW structure to shape puzzle format
          exerciseDoc.exercises.forEach((ex, index) => {
            if (ex.exercise_type === 'fill_blank' && ex.question_type === 'conjugation') {
              // Convert to sentence building format for shape puzzle
              const _sequence = ex.question ? ex.question.split(' ').filter(word => word.includes('__') || word.includes('(')) : [];
              const correctAnswer = ex.answer || '';
              const wrongOptions = Array.isArray(ex.options) ? ex.options.filter(opt => opt !== correctAnswer) : [];

              if (correctAnswer) {
                exercises.push({
                  id: `puzzle_${ex.id}`,
                  speaker: index % 2 === 0 ? 'left' : 'right',
                  sequence: [correctAnswer], // Simplified sequence for now
                  wrongOptions: wrongOptions.slice(0, 3), // Limit to 3 wrong options
                  grammarPattern: ex.tags?.grammar_focus || 'Grammar Pattern',
                  explanation: ex.instruction || 'Complete the sentence',
                  grammarIndex: index % 3,
                  category: ex.tags?.focus?.[0] || 'Grammar',
                  difficulty: ex.tags?.difficulty || 'beginner',
                  example: ex.question || '',
                  tags: ex.tags || {}
                });
              }
            }
          });
        }
      });
    } else if (level === 2 && (grammarData.source === 'schema_with_firebase_fallback' || grammarData.source === 'schema' || grammarData.source === 'schema_fallback')) {
      // For Level 2, use schema data to generate shape puzzle exercises
      if (grammarData.learning && Array.isArray(grammarData.learning)) {
        grammarData.learning.forEach((grammar, index) => {
          // Generate sentence building exercises from examples
          const sentenceExercises = GrammarSchemaUtils.generateExercisesFromGrammar(grammar, 'sentence_building');

          if (Array.isArray(sentenceExercises)) {
            sentenceExercises.forEach((exercise, exIndex) => {
              exercises.push({
                id: `puzzle_${grammar.id}_${exIndex}`,
                speaker: index % 2 === 0 ? 'left' : 'right',
                sequence: exercise.words,
                wrongOptions: this.generateWrongOptions(exercise.words.join(' ')),
                grammarPattern: grammar.grammarPoint,
                explanation: grammar.explanation,
                grammarIndex: index % 3,
                category: grammar.category,
                difficulty: grammar.difficulty,
                example: exercise.example
              });
            });
          }
        });
      }
    } else {
      // For other levels, use Firebase data with CRITICAL FIX for regular_examples location
      if (grammarData.learning && Array.isArray(grammarData.learning)) {
        grammarData.learning.forEach((grammar, index) => {
          // CRITICAL FIX: Check for regular_examples at BOTH root level AND step1 level
          const regularExamples = grammar.regular_examples || grammar.step1?.regular_examples || [];
          const step1Examples = grammar.step1?.examples || [];
          const allExamples = [...(Array.isArray(regularExamples) ? regularExamples : []), ...(Array.isArray(step1Examples) ? step1Examples : [])];

          console.log(`[SHAPE_PUZZLE] Grammar ${grammar.id}: Found ${allExamples.length} examples`);

          if (allExamples.length > 0) {
            allExamples.forEach((example, exIndex) => {
              // Handle both object examples and string examples
              const exampleText = typeof example === 'string' ? example : (example.sentence_kr || example.result || example.text || '');

              if (exampleText) {
                exercises.push({
                  id: `puzzle_${grammar.id}_${exIndex}`,
                  speaker: index % 2 === 0 ? 'left' : 'right',
                  sequence: this.parseSequence(exampleText),
                  wrongOptions: this.generateWrongOptions(exampleText),
                  grammarPattern: grammar.title_kr || grammar.step1?.explanation || 'Grammar Pattern',
                  explanation: grammar.explanation?.core_meaning || grammar.title_en || 'Grammar explanation',
                  grammarIndex: index % 3,
                  category: grammar.grammar_type || 'Grammar',
                  difficulty: 'beginner',
                  example: exampleText
                });
              }
            });
          }
        });
      }
    }

    setCachedData(cacheKey, exercises);
    return exercises;
  },

  // Generate translation matching exercises from vocabulary data
  async generateTranslationExercises(level, chapter, institution = 'Korea_University') {
    const cacheKey = getCacheKey('translation_exercises', level, chapter, institution);
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    const vocabularyData = await VocabularyService.getVocabulary(level, chapter, institution);
    const exercises = vocabularyData.map(word => ({
      korean: word.korean,
      english: word.translations?.english || '',
      spanish: word.translations?.spanish || '',
      japanese: word.translations?.japanese || '',
      portuguese: word.translations?.portuguese || ''
    }));

    setCachedData(cacheKey, exercises);
    return exercises;
  },


  // Helper methods for exercise generation
  determineDifficulty(grammar) {
    if (grammar.step1?.explanation?.includes('basic') || grammar.step1?.explanation?.includes('simple')) {
      return 'simple';
    } else if (grammar.step1?.explanation?.includes('complex') || grammar.step1?.explanation?.includes('advanced')) {
      return 'hard';
    }
    return 'medium';
  },

  // Extract grammar point ID from exercise ID
  extractGrammarPointFromExerciseId(exerciseId) {
    // Extract grammar point from NEW exercise ID patterns
    // Examples: "grm_1_001_001" -> "ch1_gmr_001", "grm_1_001_020" -> "ch1_gmr_001"
    if (exerciseId.startsWith('grm_')) {
      const parts = exerciseId.split('_');
      if (parts.length >= 3) {
        const chapter = parts[1];
        const grammarPoint = parts[2];
        return `ch${chapter}_gmr_${grammarPoint.padStart(3, '0')}`;
      }
    }
    // Legacy support for old ID patterns
    if (exerciseId.startsWith('PA_') || exerciseId.startsWith('CP_') || exerciseId.startsWith('KU_') || exerciseId.startsWith('CS_') || exerciseId.startsWith('IBP_')) {
      return 'ch1_gmr_001'; // Default to first grammar point for now
    }
    return null;
  },

  createClozeQuestion(example) {
    // Simple implementation - replace verb with blank
    return example.replace(/(\w+고\s*있어요)/, '__');
  },

  extractAnswer(example) {
    const match = example.match(/(\w+고\s*있어요)/);
    return match ? match[1] : '';
  },

  parseSequence(example) {
    return example.split(' ');
  },

  generateWrongOptions(example) {
    const words = example.split(' ');
    const wrongOptions = [];

    // Generate 3 wrong options by changing one word
    for (let i = 0; i < 3; i++) {
      const modified = [...words];
      const randomIndex = Math.floor(Math.random() * modified.length);
      modified[randomIndex] = `wrong${i + 1}`;
      wrongOptions.push(modified.join(' '));
    }

    return wrongOptions;
  },

  // NEW: Validate answer against accepted_answers array
  validateAnswer(userAnswer, exercise) {
    if (!exercise.accepted_answers || !Array.isArray(exercise.accepted_answers)) {
      // Fallback to simple answer comparison
      return userAnswer === exercise.answer;
    }

    // Check if user answer matches any accepted answer (case-insensitive)
    return exercise.accepted_answers.some(acceptedAnswer =>
      userAnswer.toLowerCase().trim() === acceptedAnswer.toLowerCase().trim()
    );
  },

  // NEW: Get exercise type classification
  getExerciseTypeClassification(exercise) {
    const tags = exercise.tags || {};
    return {
      exercise_type: exercise.exercise_type || 'fill_blank',
      question_type: exercise.question_type || 'conjugation',
      difficulty: tags.difficulty || 'simple',
      complexity: tags.complexity || ['basic_conjugation'],
      focus: tags.focus || ['progressive_construction'],
      tense: tags.tense || ['present'],
      speech_level: tags.speech_level || ['polite'],
      grammar_focus: tags.grammar_focus || 'grammar_practice'
    };
  }
};

// Progress Tracking Service
export const ProgressService = {
  // Save exercise attempt with rich tracking data
  async saveExerciseAttempt(userId, exerciseData) {
    try {
      console.group(`🔥 ProgressService: Saving exercise attempt for ${userId}`);
      console.log('Exercise data:', exerciseData);

      // Create the exercise attempt document with the specified structure
      const attemptData = {
        exercise_id: exerciseData.exercise_id,
        user_answer: exerciseData.user_answer,
        correct_answer: exerciseData.correct_answer,
        is_correct: exerciseData.is_correct,
        attempt_time: exerciseData.attempt_time || new Date().toISOString(),
        response_time_sec: exerciseData.response_time_sec,
        hint_level_used: exerciseData.hint_level_used || 0,
        timestamp: serverTimestamp()
      };

      const docRef = doc(db, 'users', userId, 'exercise_attempts', exerciseData.exercise_id);
      await setDoc(docRef, attemptData, { merge: true });

      console.log(`✅ Exercise attempt saved: ${exerciseData.exercise_id}`);
      console.groupEnd();

      // Also update lesson mastery if this is a new attempt
      await this.updateLessonMasteryFromAttempt(userId, exerciseData);

    } catch (error) {
      console.error('❌ Error saving exercise attempt:', error);
      throw new DataServiceError(
        'Failed to save exercise attempt',
        'SAVE_ERROR',
        { exerciseData, error: error.message }
      );
    }
  },

  // NEW: Save exercise attempt with session type and structured path (with offline support)
  async saveExerciseAttemptWithSession(userId, level, chapter, sessionType, exerciseData) {
    try {
      console.group(`🔥 ProgressService: Saving exercise attempt with session for ${userId}`);
      console.log('Level:', level, 'Chapter:', chapter, 'Session:', sessionType);
      console.log('Exercise data:', exerciseData);

      // Check if we're online and Firebase is available
      if (isOnline() && isFirebaseAvailable()) {
        try {
          // Create single document per module type: users/{userId}/level_{num}/chapter{num}/{sessionType}/{moduleType}
          const moduleType = exerciseData.module_type || 'unknown';
          const sessionPath = `users/${userId}/level_${level}/chapter_${chapter}/${sessionType}`;
          const docRef = doc(db, sessionPath, moduleType);

          // Get existing document
          const docSnap = await getDoc(docRef);
          let moduleData = docSnap.exists() ? docSnap.data() : {
            module_type: moduleType,
            level: level,
            chapter: chapter,
            session_type: sessionType,
            total_attempts: 0,
            attempts: {},
            created_at: serverTimestamp(),
            last_updated: serverTimestamp()
          };

          // Create attempt data with enhanced scoring
          const attemptIndex = moduleData.total_attempts + 1;
          const attemptData = {
            attempt_number: attemptIndex,
            exercise_id: exerciseData.exercise_id,
            user_answer: exerciseData.user_answer,
            correct_answer: exerciseData.correct_answer,
            is_correct: exerciseData.is_correct,
            attempt_time: exerciseData.attempt_time || new Date().toISOString(),
            response_time_sec: exerciseData.response_time_sec,
            hint_level_used: exerciseData.hint_level_used || 0,
            exercise_number: exerciseData.exercise_number || 1,
            // Enhanced scoring data
            score: exerciseData.score || (exerciseData.is_correct ? 100 : 0),
            max_score: exerciseData.max_score || 100,
            time_spent: exerciseData.time_spent || exerciseData.response_time_sec || 0,
            hints_used: exerciseData.hints_used || exerciseData.hint_level_used || 0,
            attempts: exerciseData.attempts || 1,
            weighted_value: exerciseData.weighted_value || 1.0,
            skill_category: exerciseData.skill_category || exerciseData.subcategory || 'general',
            // Additional metadata
            question_text: exerciseData.question_text || '',
            options: exerciseData.options || [],
            difficulty: exerciseData.difficulty || 'beginner',
            category: exerciseData.category || '',
            subcategory: exerciseData.subcategory || ''
          };

          // Add attempt to module data
          moduleData.attempts[`attempt_${attemptIndex}`] = attemptData;
          moduleData.total_attempts = attemptIndex;
          moduleData.last_updated = serverTimestamp();

          // Save updated module data
          await setDoc(docRef, moduleData, { merge: true });

          console.log(`✅ Exercise attempt saved to module document: ${sessionPath}/${moduleType}`);
          console.log(`📊 Attempt ${attemptIndex} added to ${moduleType} module`);

          // Update learning progress
          await this.updateLearningProgress(userId, level, chapter, sessionType, exerciseData);

          console.groupEnd();
          return true;

        } catch (firebaseError) {
          console.warn('❌ Firebase save failed, queuing for offline sync:', firebaseError);
          // Fall through to offline queue
        }
      }

      // If we're offline or Firebase failed, queue for offline sync
      console.log('📱 Queuing exercise attempt for offline sync');

      const queueData = {
        type: 'exercise_attempt',
        userId,
        level,
        chapter,
        sessionType,
        exerciseData,
        timestamp: Date.now()
      };

      console.log('📱 Queue data:', queueData);
      const queueId = OfflineProgressQueue.enqueue(queueData);

      if (queueId) {
        console.log(`✅ Exercise attempt queued for offline sync: ${queueId}`);
        console.groupEnd();
        return true;
      } else {
        throw new Error('Failed to queue exercise attempt for offline sync');
      }

    } catch (error) {
      console.error('❌ Error saving exercise attempt with session:', error);
      throw new DataServiceError(
        'Failed to save exercise attempt with session',
        'SAVE_ERROR',
        { userId, level, chapter, sessionType, exerciseData, error: error.message }
      );
    }
  },

  // NEW: Update learning progress for session with comprehensive scoring
  async updateLearningProgress(userId, level, chapter, sessionType, exerciseData) {
    try {
      console.group(`🔥 ProgressService: Updating learning progress with FIXED scoring for ${userId}`);
      console.log('Level:', level, 'Chapter:', chapter, 'Session:', sessionType);
      console.log('Exercise data:', exerciseData);

      const progressPath = `users/${userId}/level_${level}/chapter_${chapter}/${sessionType}`;
      const progressRef = doc(db, progressPath, 'LearningProgress');
      const progressSnap = await getDoc(progressRef);

      let currentProgress = progressSnap.exists() ? progressSnap.data() : {
        level: level,
        chapter: chapter,
        session_type: sessionType,
        total_exercises: 0,
        completed_exercises: 0,
        current_exercise_number: 1,
        modules: {},
        // FIXED: Proper overall metrics
        total_correct: 0,
        overall_accuracy_rate: 0,
        total_time_spent: 0,
        last_updated: serverTimestamp(),
        started_at: serverTimestamp()
      };

      // Update module progress with FIXED scoring
      const moduleType = exerciseData.module_type || 'unknown';
      if (!currentProgress.modules[moduleType]) {
        currentProgress.modules[moduleType] = {
          total_exercises: 0,
          completed_exercises: 0,
          current_exercise_number: 1,
          // FIXED: Track correct attempts, not scores
          correct_attempts: 0,
          total_time_spent: 0,
          hints_used_total: 0,
          last_updated: serverTimestamp()
        };
      }

      const module = currentProgress.modules[moduleType];

      // FIXED: Basic progress tracking
      module.total_exercises += 1;
      module.completed_exercises += 1;
      module.current_exercise_number = exerciseData.exercise_number || 1;

      // FIXED: Simple correct/incorrect tracking
      const timeSpent = exerciseData.time_spent || exerciseData.response_time_sec || 0;
      const hintsUsed = exerciseData.hints_used || exerciseData.hint_level_used || 0;

      // FIXED: Update module metrics correctly
      module.total_time_spent = (module.total_time_spent || 0) + timeSpent;
      module.hints_used_total = (module.hints_used_total || 0) + hintsUsed;

      // FIXED: Track accuracy properly
      if (exerciseData.is_correct) {
        module.correct_attempts = (module.correct_attempts || 0) + 1;
      }

      // FIXED: Calculate PERCENTAGES correctly (not cumulative scores)
      module.accuracy_rate = Math.round((module.correct_attempts / module.completed_exercises) * 100);
      module.avg_time_per_exercise = Math.round(module.total_time_spent / module.completed_exercises);
      module.avg_hints_per_exercise = Math.round((module.hints_used_total / module.completed_exercises) * 100) / 100;

      // For display purposes, average_score should be same as accuracy_rate for most exercises
      module.average_score = module.accuracy_rate;

      module.last_updated = serverTimestamp();

      // FIXED: Update overall progress metrics
      currentProgress.total_exercises += 1;
      currentProgress.completed_exercises += 1;
      currentProgress.current_exercise_number = exerciseData.exercise_number || 1;
      currentProgress.total_time_spent = (currentProgress.total_time_spent || 0) + timeSpent;

      // FIXED: Calculate overall accuracy correctly
      if (exerciseData.is_correct) {
        currentProgress.total_correct = (currentProgress.total_correct || 0) + 1;
      }
      currentProgress.overall_accuracy_rate = Math.round((currentProgress.total_correct / currentProgress.completed_exercises) * 100);

      currentProgress.last_updated = serverTimestamp();

      // Save updated progress
      await setDoc(progressRef, currentProgress, { merge: true });

      console.log(`✅ Learning progress updated for ${moduleType} with FIXED metrics`);
      console.log(`📊 Module metrics:`, {
        accuracy: module.accuracy_rate + '%',
        avgScore: module.average_score + '%',
        avgTime: Math.floor(module.avg_time_per_exercise / 60) + 'm ' + (module.avg_time_per_exercise % 60) + 's',
        hintsUsed: module.avg_hints_per_exercise
      });
      console.groupEnd();

    } catch (error) {
      console.error('❌ Error updating learning progress:', error);
      throw new DataServiceError(
        'Failed to update learning progress',
        'UPDATE_ERROR',
        { userId, level, chapter, sessionType, error: error.message }
      );
    }
  },

  // NEW: Get learning progress for session
  async getLearningProgress(userId, level, chapter, sessionType) {
    try {
      const progressPath = `users/${userId}/level_${level}/chapter_${chapter}/${sessionType}`;
      const progressRef = doc(db, progressPath, 'LearningProgress');
      const progressSnap = await getDoc(progressRef);

      if (!progressSnap.exists()) {
        return null;
      }

      return progressSnap.data();
    } catch (error) {
      console.error('❌ Error getting learning progress:', error);
      throw new DataServiceError(
        'Failed to get learning progress',
        'FETCH_ERROR',
        { userId, level, chapter, sessionType, error: error.message }
      );
    }
  },

  // NEW: Get learning modules order from Firebase with comprehensive path testing
  async getLearningModulesOrder(level, chapter) {
    try {
      console.group(`🔥 ProgressService.getLearningModulesOrder: Fetching from Firebase`);
      console.log(`📊 Level: ${level}, Chapter: ${chapter}`);

      // First, test Firebase connection and walk the structure
      await this.testFirebaseConnectionAndWalkStructure();

      // Try multiple path combinations
      const pathCombinations = [
        // Target path
        'feature_testing/Korea_University/LearningModulesOrder/order',
        // Alternative paths
        'feature_testing/Korea_University/LearningModulesOrder',
        'feature_testing/Korea_University',
        'feature_testing',
        'LearningModulesOrder/order'
      ];

      for (const path of pathCombinations) {
        try {
          console.log(`\n🔍 Testing path: ${path}`);

          if (path.includes('/order')) {
            // Try as document
            const orderRef = doc(db, path.replace('/order', ''), 'order');
            console.log(`📄 Document reference:`, orderRef);
            console.log(`📄 Document path:`, orderRef.path);

            const orderSnap = await getDoc(orderRef);
            console.log(`📥 Document snapshot exists:`, orderSnap.exists());

            if (orderSnap.exists()) {
              const orderData = orderSnap.data();
              console.log(`✅ SUCCESS: Found document at ${path}`);
              console.log(`📦 Raw Firebase data:`, orderData);

              const levelKey = `level_${level}`;
              const chapterKey = `chapter_${chapter}`;
              console.log(`🔑 Looking for: ${levelKey}.${chapterKey}`);

              if (orderData[levelKey]) {
                console.log(`✅ Found level data:`, orderData[levelKey]);
                if (orderData[levelKey][chapterKey]) {
                  const modulesOrder = orderData[levelKey][chapterKey];
                  console.log(`✅ Found chapter modules order:`, modulesOrder);
                  console.groupEnd();
                  return modulesOrder;
                } else {
                  console.warn(`⚠️ Chapter ${chapterKey} not found in level ${levelKey}`);
                }
              } else {
                console.warn(`⚠️ Level ${levelKey} not found in Firebase data`);
              }

              console.log('📋 Returning default modules order (level/chapter not found)');
              console.groupEnd();
              return ['dialogue', 'vocabulary', 'translation', 'grammar', 'cloze', 'shapePuzzle'];
            }
          } else {
            // Try as collection
            const collectionRef = collection(db, path);
            console.log(`📁 Collection reference:`, collectionRef);
            console.log(`📁 Collection path:`, collectionRef.path);

            const collectionSnap = await getDocs(collectionRef);
            console.log(`📥 Collection snapshot size:`, collectionSnap.size);

            if (collectionSnap.size > 0) {
              console.log(`✅ SUCCESS: Found collection at ${path} with ${collectionSnap.size} documents`);
              collectionSnap.forEach((doc) => {
                console.log(`📄 Document ID: ${doc.id}`);
                console.log(`📄 Document data keys:`, Object.keys(doc.data()));
              });
            }
          }
        } catch (pathError) {
          console.error(`❌ Path ${path} failed:`, pathError.message);
        }
      }

      console.warn('⚠️ All path combinations failed - returning default modules order');
      console.groupEnd();
      return ['dialogue', 'vocabulary', 'translation', 'grammar', 'cloze', 'shapePuzzle'];

    } catch (error) {
      console.error('❌ Error getting learning modules order:', error);
      console.error('❌ Error details:', {
        message: error.message,
        code: error.code,
        level,
        chapter
      });
      console.groupEnd();
      // Return default order on error
      return ['dialogue', 'vocabulary', 'translation', 'grammar', 'cloze', 'shapePuzzle'];
    }
  },

  // NEW: Test Firebase connection and walk the structure
  async testFirebaseConnectionAndWalkStructure() {
    try {
      console.group(`🔥 ProgressService: Testing Firebase Connection and Walking Structure`);

      // Test basic Firebase connection
      console.log(`🔥 Firebase db instance:`, !!db);
      console.log(`🔥 Firebase auth instance:`, !!auth);

      // Walk the structure starting from root
      await this.walkFirebasePath('feature_testing');

      console.groupEnd();
    } catch (error) {
      console.error('❌ Firebase connection test failed:', error);
      console.groupEnd();
    }
  },

  // NEW: Walk Firebase path structure
  async walkFirebasePath(path) {
    try {
      console.log(`\n🚶 Walking Firebase path: ${path}`);

      // Try as collection first
      try {
        const collectionRef = collection(db, path);
        console.log(`📁 Collection reference:`, collectionRef);
        console.log(`📁 Collection path:`, collectionRef.path);

        const collectionSnap = await getDocs(collectionRef);
        console.log(`📥 Collection snapshot size:`, collectionSnap.size);

        if (collectionSnap.size > 0) {
          console.log(`✅ Found collection at ${path} with ${collectionSnap.size} documents:`);
          collectionSnap.forEach((doc) => {
            console.log(`  📄 Document ID: ${doc.id}`);
            console.log(`  📄 Document data keys:`, Object.keys(doc.data()));

            // If this is a document we're interested in, show more details
            if (doc.id === 'order' || doc.id === 'Korea_University') {
              console.log(`  📄 Document data:`, doc.data());
            }
          });

          // Recursively walk subcollections for interesting paths
          if (path === 'feature_testing') {
            for (const doc of collectionSnap.docs) {
              if (doc.id === 'Korea_University') {
                await this.walkFirebasePath(`${path}/${doc.id}`);
              }
            }
          } else if (path.includes('Korea_University')) {
            for (const doc of collectionSnap.docs) {
              if (doc.id === 'LearningModulesOrder') {
                await this.walkFirebasePath(`${path}/${doc.id}`);
              }
            }
          }
        } else {
          console.log(`📭 Collection at ${path} is empty`);
        }
      } catch (collectionError) {
        console.log(`❌ Failed to access ${path} as collection:`, collectionError.message);

        // Try as document
        try {
          const docRef = doc(db, path);
          console.log(`📄 Document reference:`, docRef);
          console.log(`📄 Document path:`, docRef.path);

          const docSnap = await getDoc(docRef);
          console.log(`📥 Document snapshot exists:`, docSnap.exists());

          if (docSnap.exists()) {
            console.log(`✅ Found document at ${path}`);
            console.log(`📄 Document data keys:`, Object.keys(docSnap.data()));
            console.log(`📄 Document data:`, docSnap.data());
          } else {
            console.log(`📭 Document at ${path} does not exist`);
          }
        } catch (docError) {
          console.log(`❌ Failed to access ${path} as document:`, docError.message);
        }
      }
    } catch (error) {
      console.error(`❌ Error walking path ${path}:`, error);
    }
  },

  // NEW: Create session progress document
  async createSessionProgress(userId, level, chapter, sessionType) {
    try {
      console.group(`🔥 ProgressService: Creating session progress for ${userId}`);
      console.log('Level:', level, 'Chapter:', chapter, 'Session:', sessionType);

      const progressPath = `users/${userId}/level_${level}/chapter_${chapter}/${sessionType}`;
      const progressRef = doc(db, progressPath, 'LearningProgress');

      // Get learning modules order
      const modulesOrder = await this.getLearningModulesOrder(level, chapter);

      const initialProgress = {
        level: level,
        chapter: chapter,
        session_type: sessionType,
        total_exercises: 0,
        completed_exercises: 0,
        current_exercise_number: 1,
        modules: {},
        modules_order: modulesOrder,
        last_updated: serverTimestamp(),
        started_at: serverTimestamp()
      };

      // Initialize module progress
      modulesOrder.forEach(moduleType => {
        initialProgress.modules[moduleType] = {
          total_exercises: 0,
          completed_exercises: 0,
          current_exercise_number: 1,
          last_updated: serverTimestamp()
        };
      });

      await setDoc(progressRef, initialProgress, { merge: true });

      console.log(`✅ Session progress created: ${progressPath}/LearningProgress`);
      console.groupEnd();

      return initialProgress;
    } catch (error) {
      console.error('❌ Error creating session progress:', error);
      throw new DataServiceError(
        'Failed to create session progress',
        'CREATE_ERROR',
        { userId, level, chapter, sessionType, error: error.message }
      );
    }
  },

  // NEW: Upload LearningModulesOrder to Firebase
  async uploadLearningModulesOrder(modulesOrderData) {
    try {
      console.group(`🔥 ProgressService: Uploading LearningModulesOrder to Firebase`);
      console.log('Modules order data:', modulesOrderData);

      // Upload to Firebase at the path: feature_testing/Korea_University/LearningModulesOrder/order
      const orderRef = doc(db, 'feature_testing/Korea_University/LearningModulesOrder', 'order');

      const uploadData = {
        ...modulesOrderData,
        uploaded_at: serverTimestamp(),
        uploaded_by: 'system', // Could be enhanced to track user who uploaded
        version: '1.0'
      };

      await setDoc(orderRef, uploadData, { merge: true });

      console.log(`✅ LearningModulesOrder uploaded successfully to Firebase`);
      console.log(`📁 Path: feature_testing/Korea_University/LearningModulesOrder/order`);
      console.groupEnd();

      return {
        success: true,
        path: 'feature_testing/Korea_University/LearningModulesOrder/order',
        data: uploadData
      };

    } catch (error) {
      console.error('❌ Error uploading LearningModulesOrder:', error);
      throw new DataServiceError(
        'Failed to upload LearningModulesOrder',
        'UPLOAD_ERROR',
        { modulesOrderData, error: error.message }
      );
    }
  },

  // Update lesson mastery with comprehensive tracking
  async updateLessonMastery(userId, lessonId, masteryData) {
    try {
      console.group(`🔥 ProgressService: Updating lesson mastery for ${userId}`);
      console.log('Lesson ID:', lessonId);
      console.log('Mastery data:', masteryData);

      const masteryDoc = {
        lesson_id: lessonId,
        mastery: masteryData,
        lastUpdated: serverTimestamp()
      };

      const docRef = doc(db, 'users', userId, 'lesson_mastery', lessonId);
      await setDoc(docRef, masteryDoc, { merge: true });

      console.log(`✅ Lesson mastery updated: ${lessonId}`);
      console.groupEnd();

    } catch (error) {
      console.error('❌ Error updating lesson mastery:', error);
      throw new DataServiceError(
        'Failed to update lesson mastery',
        'UPDATE_ERROR',
        { lessonId, masteryData, error: error.message }
      );
    }
  },

  // Update lesson mastery from exercise attempt
  async updateLessonMasteryFromAttempt(userId, exerciseData) {
    try {
      // Extract lesson ID from exercise ID (format: L{level}_CH{chapter}_{subject}_{subcategory}_{exercise_number})
      const lessonMatch = exerciseData.exercise_id.match(/^(L\d+_CH\d+)/);
      if (!lessonMatch) {
        console.warn('Could not extract lesson ID from exercise ID:', exerciseData.exercise_id);
        return;
      }

      const lessonId = lessonMatch[1];

      // Get current mastery data
      const currentMastery = await this.getLessonMastery(userId, lessonId);

      // Determine the skill type based on exercise subcategory
      const skillType = this.determineSkillType(exerciseData.exercise_id);

      // Update mastery statistics
      const updatedMastery = this.calculateMasteryUpdate(currentMastery, exerciseData, skillType);

      // Save updated mastery
      await this.updateLessonMastery(userId, lessonId, updatedMastery);

    } catch (error) {
      console.error('❌ Error updating lesson mastery from attempt:', error);
    }
  },

  // Get lesson mastery for a specific lesson
  async getLessonMastery(userId, lessonId) {
    try {
      const docRef = doc(db, 'users', userId, 'lesson_mastery', lessonId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        // Return default mastery structure
        return {
          recognition: {
            total_exercises: 0,
            avg_time_sec: 0,
            first_attempts_total: 0,
            first_attempts_correct: 0,
            first_attempts_incorrect: 0,
            first_attempt_correct_rate: 0,
            total_attempts: 0,
            total_attempts_correct: 0,
            total_attempts_incorrect: 0,
            overall_accuracy_rate: 0
          },
          production: {
            total_exercises: 0,
            avg_time_sec: 0,
            first_attempts_total: 0,
            first_attempts_correct: 0,
            first_attempts_incorrect: 0,
            first_attempt_correct_rate: 0,
            total_attempts: 0,
            total_attempts_correct: 0,
            total_attempts_incorrect: 0,
            overall_accuracy_rate: 0
          },
          application: {
            total_exercises: 0,
            avg_time_sec: 0,
            first_attempts_total: 0,
            first_attempts_correct: 0,
            first_attempts_incorrect: 0,
            first_attempt_correct_rate: 0,
            total_attempts: 0,
            total_attempts_correct: 0,
            total_attempts_incorrect: 0,
            overall_accuracy_rate: 0
          }
        };
      }

      return docSnap.data().mastery;
    } catch (error) {
      console.error('❌ Error fetching lesson mastery:', error);
      throw new DataServiceError(
        'Failed to fetch lesson mastery',
        'FETCH_ERROR',
        { userId, lessonId, error: error.message }
      );
    }
  },

  // Get user progress
  async getUserProgress(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        return null;
      }

      return userSnap.data();
    } catch (error) {
      console.error('❌ Error fetching user progress:', error);
      throw new DataServiceError(
        'Failed to fetch user progress',
        'FETCH_ERROR',
        { userId, error: error.message }
      );
    }
  },

  // Helper methods for mastery tracking
  determineSkillType(exerciseId) {
    // Map exercise subcategories to skill types
    if (exerciseId.includes('grammar_fillblank') || exerciseId.includes('cloze')) {
      return 'production';
    } else if (exerciseId.includes('grammar_judgment') || exerciseId.includes('comprehension')) {
      return 'recognition';
    } else if (exerciseId.includes('translation') || exerciseId.includes('application')) {
      return 'application';
    }
    return 'production'; // default
  },

  calculateMasteryUpdate(currentMastery, exerciseData, skillType) {
    const mastery = { ...currentMastery };
    const skill = mastery[skillType] || {
      total_exercises: 0,
      avg_time_sec: 0,
      first_attempts_total: 0,
      first_attempts_correct: 0,
      first_attempts_incorrect: 0,
      first_attempt_correct_rate: 0,
      total_attempts: 0,
      total_attempts_correct: 0,
      total_attempts_incorrect: 0,
      overall_accuracy_rate: 0
    };

    // Update statistics
    skill.total_exercises += 1;
    skill.total_attempts += 1;

    if (exerciseData.is_correct) {
      skill.total_attempts_correct += 1;
    } else {
      skill.total_attempts_incorrect += 1;
    }

    // Update average time
    const totalTime = skill.avg_time_sec * (skill.total_attempts - 1) + (exerciseData.response_time_sec || 0);
    skill.avg_time_sec = totalTime / skill.total_attempts;

    // Update accuracy rates
    skill.overall_accuracy_rate = skill.total_attempts_correct / skill.total_attempts;

    // For first attempts, we'd need to track this separately
    // This is a simplified version - in practice, you'd need to track first attempts per exercise
    skill.first_attempts_total += 1;
    if (exerciseData.is_correct) {
      skill.first_attempts_correct += 1;
    } else {
      skill.first_attempts_incorrect += 1;
    }
    skill.first_attempt_correct_rate = skill.first_attempts_correct / skill.first_attempts_total;

    mastery[skillType] = skill;
    return mastery;
  }
};

// Content Discovery Service
export const ContentService = {
  // Get available content overview
  async getAvailableContent(institution = 'Korea_University') {
    const cacheKey = getCacheKey('available_content', institution);
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    const content = {
      levels: {},
      totalLevels: 0,
      totalChapters: 0,
      totalGrammar: 0,
      totalVocabulary: 0
    };

    // Check each level (1-6)
    for (let level = 1; level <= 6; level++) {
      try {
        const grammarChapters = await GrammarService.getAvailableChapters(level, institution);
        const vocabularyChapters = await VocabularyService.getAvailableChapters(level, institution);

        if (grammarChapters.length > 0 || vocabularyChapters.length > 0) {
          content.levels[level] = {
            hasGrammar: grammarChapters.length > 0,
            hasVocabulary: vocabularyChapters.length > 0,
            grammarChapters: grammarChapters,
            vocabularyChapters: vocabularyChapters,
            totalChapters: Math.max(grammarChapters.length, vocabularyChapters.length)
          };

          content.totalLevels++;
          content.totalChapters += content.levels[level].totalChapters;
        }
      } catch (error) {
        console.warn(`Failed to check level ${level}:`, error);
      }
    }

    setCachedData(cacheKey, content);
    return content;
  },

  // Search across all content
  async searchContent(searchTerm, institution = 'Korea_University') {
    const cacheKey = getCacheKey('content_search', searchTerm, institution);
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    const results = {
      grammar: [],
      vocabulary: [],
      total: 0
    };

    // Search vocabulary
    results.vocabulary = await VocabularyService.searchVocabulary(searchTerm, institution);

    // Search grammar (simplified for now)
    for (let level = 1; level <= 6; level++) {
      try {
        const grammarChapters = await GrammarService.getAvailableChapters(level, institution);
        for (const chapter of grammarChapters) {
          const grammarData = await GrammarService.getGrammarPoints(level, chapter, institution);
          const filtered = grammarData.learning.filter(doc =>
            doc.step1?.explanation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            doc.step1?.nuance?.toLowerCase().includes(searchTerm.toLowerCase())
          );

          results.grammar.push(...filtered.map(item => ({ ...item, level, chapter })));
        }
      } catch (error) {
        console.warn(`Failed to search grammar level ${level}:`, error);
      }
    }

    results.total = results.grammar.length + results.vocabulary.length;

    setCachedData(cacheKey, results);
    return results;
  }
};

// Utility functions
export const DataUtils = {
  // Clear all cache
  clearAllCache: () => clearCache(),

  // Clear cache for specific type
  clearCacheForType: (type) => clearCache(type),

  // Get cache statistics
  getCacheStats: () => {
    const stats = {
      totalEntries: cache.size,
      entries: []
    };

    for (const [key, value] of cache.entries()) {
      stats.entries.push({
        key,
        age: Date.now() - value.timestamp,
        size: JSON.stringify(value.data).length
      });
    }

    return stats;
  },

  // Validate data structure
  validateData: (data, expectedFields) => {
    const missing = expectedFields.filter(field => !(field in data));
    if (missing.length > 0) {
      throw new DataServiceError(
        `Missing required fields: ${missing.join(', ')}`,
        'VALIDATION_ERROR',
        { missing, data }
      );
    }
    return true;
  }
};

// Export all services including offline system (LEGACY VERSION - kept for backward compatibility)
const _DataServiceLegacy = {
  GrammarService,
  VocabularyService,
  ExerciseService,
  ProgressService,
  ContentService,
  DataUtils,
  UserGrammarTracking,
  db,
  auth,

  // Offline system exports
  OfflineStorage,
  OfflineProgressQueue,
  OfflineContentCache,
  OfflineSync,
  OfflineIndicators,

  // Offline utility functions
  isOnline,
  isFirebaseAvailable,

  // Offline management functions
  initializeOfflineSystem,
  getOfflineFallback
};

// 🎯 INDIVIDUAL CHAPTER MODULE ORDER MANAGEMENT
// New methods for CRUD operations on individual chapters

/**
 * Update modules order for a specific chapter
 * 
 * @param {number} level - Level number (1, 2, 3, 4)
 * @param {number} chapter - Chapter number (1-10)
 * @param {Array} modulesOrder - Array of module IDs in desired order
 * @returns {Promise<Object>} Update result with success status
 */
export const updateChapterModulesOrder = async (level, chapter, modulesOrder) => {
  try {
    console.group(`💾 Updating chapter modules order`);
    console.log('Level:', level, 'Chapter:', chapter);
    console.log('New modules order:', modulesOrder);

    // Get current full data structure
    const currentData = await ProgressService.getLearningModulesOrder(level, chapter);
    console.log('Current data structure:', currentData);

    // Update the specific chapter in the full structure
    const levelKey = `level_${level}`;
    const chapterKey = `chapter_${chapter}`;

    // First, get the current full order data
    const docRef = doc(db, 'feature_testing/Korea_University/LearningModulesOrder/order');
    const docSnap = await getDoc(docRef);

    let fullOrderData = {};
    if (docSnap.exists()) {
      fullOrderData = docSnap.data();
    } else {
      // Initialize with default structure if document doesn't exist
      fullOrderData = learningModulesOrderData;
    }

    // Update the specific chapter
    if (!fullOrderData[levelKey]) {
      fullOrderData[levelKey] = {};
    }
    fullOrderData[levelKey][chapterKey] = modulesOrder;

    // Add metadata
    fullOrderData.updated_at = serverTimestamp();
    fullOrderData.last_updated_by = 'admin';
    fullOrderData.last_updated_chapter = `${levelKey}_${chapterKey}`;

    // Save back to Firebase
    await setDoc(docRef, fullOrderData, { merge: true });

    console.log('✅ Chapter modules order updated successfully');
    console.log('Updated data:', fullOrderData[levelKey][chapterKey]);
    console.groupEnd();

    return {
      success: true,
      level,
      chapter,
      modulesOrder,
      updatedAt: new Date().toISOString()
    };

  } catch (error) {
    console.error('❌ Error updating chapter modules order:', error);
    console.groupEnd();
    return {
      success: false,
      error: error.message,
      level,
      chapter,
      modulesOrder
    };
  }
};

/**
 * Update all chapters in a specific level with the same modules order
 * 
 * @param {number} level - Level number (1, 2, 3, 4)
 * @param {Array} modulesOrder - Array of module IDs in desired order
 * @returns {Promise<Object>} Update result with success status and count
 */
export const updateAllChaptersInLevel = async (level, modulesOrder) => {
  try {
    console.group(`💾 Updating ALL chapters in Level ${level}`);
    console.log('Modules order to apply:', modulesOrder);

    // Get current full data structure
    const docRef = doc(db, 'feature_testing/Korea_University/LearningModulesOrder/order');
    const docSnap = await getDoc(docRef);

    let fullOrderData = {};
    if (docSnap.exists()) {
      fullOrderData = docSnap.data();
    } else {
      // Initialize with default structure if document doesn't exist
      fullOrderData = learningModulesOrderData;
    }

    // Update all chapters in the specified level
    const levelKey = `level_${level}`;
    if (!fullOrderData[levelKey]) {
      fullOrderData[levelKey] = {};
    }

    let updatedChapters = 0;
    // Update all 10 chapters in the level
    for (let chapter = 1; chapter <= 10; chapter++) {
      const chapterKey = `chapter_${chapter}`;
      fullOrderData[levelKey][chapterKey] = [...modulesOrder]; // Create a copy
      updatedChapters++;
    }

    // Add metadata
    fullOrderData.updated_at = serverTimestamp();
    fullOrderData.last_updated_by = 'admin';
    fullOrderData.last_updated_operation = `bulk_level_${level}`;

    // Save back to Firebase
    await setDoc(docRef, fullOrderData, { merge: true });

    console.log(`✅ Successfully updated ${updatedChapters} chapters in Level ${level}`);
    console.groupEnd();

    return {
      success: true,
      level,
      updatedChapters,
      modulesOrder,
      updatedAt: new Date().toISOString()
    };

  } catch (error) {
    console.error('❌ Error updating all chapters in level:', error);
    console.groupEnd();
    return {
      success: false,
      error: error.message,
      level,
      modulesOrder
    };
  }
};

/**
 * Update all levels and all chapters with the same modules order
 * 
 * @param {Array} modulesOrder - Array of module IDs in desired order
 * @returns {Promise<Object>} Update result with success status and counts
 */
export const updateAllLevelsAndChapters = async (modulesOrder) => {
  try {
    console.group(`💾 Updating ALL levels and chapters`);
    console.log('Modules order to apply:', modulesOrder);

    // Get current full data structure
    const docRef = doc(db, 'feature_testing/Korea_University/LearningModulesOrder/order');
    const docSnap = await getDoc(docRef);

    let fullOrderData = {};
    if (docSnap.exists()) {
      fullOrderData = docSnap.data();
    } else {
      // Initialize with default structure if document doesn't exist
      fullOrderData = learningModulesOrderData;
    }

    let updatedLevels = 0;
    let updatedChapters = 0;

    // Update all 4 levels
    for (let level = 1; level <= 4; level++) {
      const levelKey = `level_${level}`;
      if (!fullOrderData[levelKey]) {
        fullOrderData[levelKey] = {};
      }

      // Update all 10 chapters in each level
      for (let chapter = 1; chapter <= 10; chapter++) {
        const chapterKey = `chapter_${chapter}`;
        fullOrderData[levelKey][chapterKey] = [...modulesOrder]; // Create a copy
        updatedChapters++;
      }
      updatedLevels++;
    }

    // Add metadata
    fullOrderData.updated_at = serverTimestamp();
    fullOrderData.last_updated_by = 'admin';
    fullOrderData.last_updated_operation = 'bulk_all_levels_chapters';

    // Save back to Firebase
    await setDoc(docRef, fullOrderData, { merge: true });

    console.log(`✅ Successfully updated ${updatedLevels} levels and ${updatedChapters} chapters`);
    console.groupEnd();

    return {
      success: true,
      updatedLevels,
      updatedChapters,
      modulesOrder,
      updatedAt: new Date().toISOString()
    };

  } catch (error) {
    console.error('❌ Error updating all levels and chapters:', error);
    console.groupEnd();
    return {
      success: false,
      error: error.message,
      modulesOrder
    };
  }
};

// Add to ProgressService object
ProgressService.updateChapterModulesOrder = updateChapterModulesOrder;
ProgressService.updateAllChaptersInLevel = updateAllChaptersInLevel;
ProgressService.updateAllLevelsAndChapters = updateAllLevelsAndChapters;

// ============================================================================
// LEARNING MODULES ORDER SERVICE (NEW STRUCTURE)
// ============================================================================

/**
 * Fetch modules order from Firebase with new document structure
 * 
 * @returns {Promise<Object>} Complete modules order data with defaultOrder, level_X.chapter_Y pattern, and moduleMap
 */
export const getLearningModulesOrder = async () => {
  try {
    console.group('🔥 getLearningModulesOrder: Fetching from Firebase');

    const ref = doc(db, 'feature_testing/Korea_University/LearningModulesOrder', 'order');
    const snap = await getDoc(ref);

    if (!snap.exists()) {
      console.warn('⚠️ Modules order document not found, returning default structure');
      console.groupEnd();
      throw new Error('Modules order not found');
    }

    const data = snap.data();
    console.log('✅ Modules order fetched successfully');
    console.log('📦 Data structure:', {
      hasDefaultOrder: !!data.defaultOrder,
      hasModuleMap: !!data.moduleMap,
      levels: Object.keys(data).filter(key => key.startsWith('level_')),
      lastUpdated: data.lastUpdated || data.last_updated
    });
    console.groupEnd();

    return data;
  } catch (error) {
    console.error('❌ Error fetching learning modules order:', error);
    console.groupEnd();
    throw new DataServiceError(
      'Failed to fetch learning modules order',
      'FETCH_ERROR',
      { error: error.message }
    );
  }
};

/**
 * Get learning modules configuration for a specific level and chapter
 * Returns both modulesOrder and moduleMap
 * @param {number} level - Level number
 * @param {number} chapter - Chapter number
 * @returns {Object} Configuration with modulesOrder and moduleMap
 */
/**
 * Deep-merge a moduleMap (from Firestore or fallback JSON) over the canonical
 * default registry (componentScanner). Firestore values win per-field, but a
 * missing/absent component field falls back to the default component so the
 * chapter runner can always resolve a module to a component.
 */
const mergeModuleMapWithDefaults = (moduleMap = {}) => {
  const defaults = getDefaultModuleMap();
  const merged = {};
  new Set([...Object.keys(defaults), ...Object.keys(moduleMap)]).forEach((key) => {
    merged[key] = { ...(defaults[key] || {}), ...(moduleMap[key] || {}) };
    if (!merged[key].component && defaults[key]?.component) {
      merged[key].component = defaults[key].component;
    }
  });
  return merged;
};

export const getLearningModulesConfig = async (level = 2, chapter = 1) => {
  try {
    console.group('🔍 getLearningModulesConfig: Loading LearningModulesOrder (preserve structure)');
    console.log('Level:', level, 'Chapter:', chapter);

    // CRITICAL FIX: Validate inputs before making Firestore call
    if (!level || !chapter) {
      console.warn('⚠️ getLearningModulesConfig: Missing level or chapter');
      console.groupEnd();
      return {
        modulesOrder: [],
        moduleMap: {},
        lastUpdated: null,
        dataSource: 'invalid_input'
      };
    }

    // CRITICAL FIX: Construct Firestore path correctly
    // Path: feature_testing/Korea_University/LearningModulesOrder/order
    // This is a document path (4 segments = even, correct)
    const docRef = doc(db, 'feature_testing', 'Korea_University', 'LearningModulesOrder', 'order');
    console.log('📁 Firestore path: feature_testing/Korea_University/LearningModulesOrder/order');

    // CRITICAL FIX: Add timeout to Firestore call to prevent infinite loading
    const firestoreTimeout = 10000; // 10 seconds
    const firestorePromise = getDoc(docRef);
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Firestore request timeout')), firestoreTimeout);
    });

    let docSnap;
    try {
      docSnap = await Promise.race([firestorePromise, timeoutPromise]);
    } catch (timeoutError) {
      console.warn('⚠️ Firestore request timed out, using fallback data');
      throw timeoutError; // Will be caught by outer catch and use fallback
    }

    if (!docSnap.exists()) {
      console.warn('⚠️ LearningModulesOrder document not found at Firestore path.');
      console.warn('⚠️ Using fallback modules order from JSON file.');
      console.groupEnd();

      // CRITICAL FIX: Return fallback data instead of empty arrays
      // Import fallback from JSON file
      try {
        const fallbackData = learningModulesOrderData;
        const levelKey = `level_${level}`;
        const chapterKey = `chapter_${chapter}`;

        let modulesOrder = [];
        let moduleMap = mergeModuleMapWithDefaults(fallbackData.moduleMap || {});

        if (fallbackData[levelKey] && fallbackData[levelKey][chapterKey]) {
          modulesOrder = [...fallbackData[levelKey][chapterKey]];
        } else if (fallbackData.defaultOrder) {
          modulesOrder = [...fallbackData.defaultOrder];
        }

        // Ensure chapterTitle is first if it exists in modulesOrder
        if (modulesOrder.length > 0 && modulesOrder.includes('chapterTitle') && modulesOrder[0] !== 'chapterTitle') {
          console.log('🔄 Reordering fallback: Moving chapterTitle to first position');
          modulesOrder = ['chapterTitle', ...modulesOrder.filter(m => m !== 'chapterTitle')];
        }

        console.log('✅ Using fallback modules order:', modulesOrder);
        return {
          modulesOrder,
          moduleMap,
          lastUpdated: null,
          dataSource: 'fallback_json'
        };
      } catch (fallbackError) {
        console.error('❌ Failed to load fallback data:', fallbackError);
        return {
          modulesOrder: [],
          moduleMap: {},
          lastUpdated: null,
          dataSource: 'fallback_failed'
        };
      }
    }

    const raw = docSnap.data();
    console.log('Raw LearningModulesOrder:', raw);

    // Try nested level_x.chapter_y first (exact keys, no normalization)
    const levelKey = `level_${level}`;
    const chapterKey = `chapter_${chapter}`;

    let modulesOrder = [];
    let moduleMap = mergeModuleMapWithDefaults(raw.moduleMap || {});
    let dataSource = 'firestore';

    if (raw[levelKey] && raw[levelKey][chapterKey] && Array.isArray(raw[levelKey][chapterKey]) && raw[levelKey][chapterKey].length > 0) {
      // Use the exact array stored under level_X.chapter_Y
      modulesOrder = [...raw[levelKey][chapterKey]];
      dataSource = `firestore_nested_${levelKey}.${chapterKey}`;
      console.log('Using nested modulesOrder at', `${levelKey}.${chapterKey}`, modulesOrder);
    } else if (Array.isArray(raw.defaultOrder) && raw.defaultOrder.length > 0) {
      // Fallback to defaultOrder exactly as stored
      modulesOrder = [...raw.defaultOrder];
      dataSource = 'firestore_defaultOrder';
      console.warn('Using defaultOrder fallback from Firestore.');
    } else {
      console.error('No modulesOrder found in LearningModulesOrder; modulesOrder empty.');
      modulesOrder = [];
    }

    // Ensure chapterTitle is first if it exists in modulesOrder
    if (modulesOrder.length > 0 && modulesOrder.includes('chapterTitle') && modulesOrder[0] !== 'chapterTitle') {
      console.log('🔄 Reordering: Moving chapterTitle to first position');
      modulesOrder = ['chapterTitle', ...modulesOrder.filter(m => m !== 'chapterTitle')];
    }

    const result = {
      modulesOrder,
      moduleMap,
      lastUpdated: raw.lastUpdated || raw.last_updated || raw.registryTimestamp || null,
      dataSource
    };

    console.log('getLearningModulesConfig result:', result);
    console.groupEnd();
    return result;
  } catch (err) {
    console.error('❌ getLearningModulesConfig failed:', err);
    console.error('Error details:', {
      message: err.message,
      code: err.code,
      level,
      chapter
    });

    // CRITICAL FIX: Return fallback data instead of throwing error
    // This prevents the component from being stuck in loading state
    try {
      console.warn('⚠️ Attempting to use fallback data from JSON file');
      const fallbackData = learningModulesOrderData;
      const levelKey = `level_${level}`;
      const chapterKey = `chapter_${chapter}`;

      let modulesOrder = [];
      let moduleMap = mergeModuleMapWithDefaults(fallbackData.moduleMap || {});

      if (fallbackData[levelKey] && fallbackData[levelKey][chapterKey]) {
        modulesOrder = [...fallbackData[levelKey][chapterKey]];
      } else if (fallbackData.defaultOrder) {
        modulesOrder = [...fallbackData.defaultOrder];
      }

      // Ensure chapterTitle is first if it exists in modulesOrder
      if (modulesOrder.length > 0 && modulesOrder.includes('chapterTitle') && modulesOrder[0] !== 'chapterTitle') {
        console.log('🔄 Reordering error fallback: Moving chapterTitle to first position');
        modulesOrder = ['chapterTitle', ...modulesOrder.filter(m => m !== 'chapterTitle')];
      }

      console.log('✅ Using fallback modules order after error:', modulesOrder);
      console.groupEnd();

      return {
        modulesOrder,
        moduleMap,
        lastUpdated: null,
        dataSource: 'fallback_json_error'
      };
    } catch (fallbackError) {
      console.error('❌ Fallback also failed:', fallbackError);
      console.groupEnd();
      // Return empty arrays as last resort - UI will show appropriate message
      return {
        modulesOrder: [],
        moduleMap: {},
        lastUpdated: null,
        dataSource: 'error_no_fallback'
      };
    }
  }
};

/**
 * Get modules order for specific level and chapter with fallback to defaultOrder
 * 
 * @param {number} level - Level number (1, 2, 3, 4)
 * @param {number} chapter - Chapter number (1-10)
 * @returns {Promise<Array>} Array of module IDs in order
 */
export const getModulesOrderForChapter = async (level, chapter) => {
  try {
    console.group(`🔥 getModulesOrderForChapter: Level ${level} Chapter ${chapter}`);

    const modulesOrderData = await getLearningModulesOrder();
    const levelKey = `level_${level}`;
    const chapterKey = `chapter_${chapter}`;

    // Check if specific chapter exists
    if (modulesOrderData[levelKey] && modulesOrderData[levelKey][chapterKey]) {
      const chapterOrder = modulesOrderData[levelKey][chapterKey];
      console.log(`✅ Found specific order for Level ${level} Chapter ${chapter}:`, chapterOrder);
      console.groupEnd();
      return chapterOrder;
    }

    // Fallback to defaultOrder
    if (modulesOrderData.defaultOrder && Array.isArray(modulesOrderData.defaultOrder)) {
      console.log(`📋 Using defaultOrder for Level ${level} Chapter ${chapter}:`, modulesOrderData.defaultOrder);
      console.groupEnd();
      return [...modulesOrderData.defaultOrder];
    }

    // Final fallback to hardcoded default
    const fallbackOrder = ['chapterTitle', 'dialogue', 'vocabulary', 'translation', 'grammar', 'cloze', 'shapePuzzle', 'complete'];
    console.log(`🔄 Using fallback order for Level ${level} Chapter ${chapter}:`, fallbackOrder);
    console.groupEnd();
    return fallbackOrder;

  } catch (error) {
    console.error('❌ Error getting modules order for chapter:', error);
    console.groupEnd();

    // Return fallback order on error
    return ['chapterTitle', 'dialogue', 'vocabulary', 'translation', 'grammar', 'cloze', 'shapePuzzle', 'complete'];
  }
};

/**
 * Get module mapping configuration
 * 
 * @returns {Promise<Object>} Module mapping with routePath and component information
 */
export const getModuleMapping = async () => {
  try {
    console.group('🔥 getModuleMapping: Fetching module mapping');

    const modulesOrderData = await getLearningModulesOrder();

    if (modulesOrderData.moduleMap) {
      console.log('✅ Module mapping fetched successfully');
      console.log('📦 Module mapping:', modulesOrderData.moduleMap);
      console.groupEnd();
      return modulesOrderData.moduleMap;
    }

    // Return default mapping if not found
    const defaultMapping = {
      'dialogue': { displayName: 'Dialogue Practice', routePath: 'dialogue', component: 'DialogueFeature' },
      'vocabulary': { displayName: 'Vocabulary Practice', routePath: 'vocabulary', component: 'VocabularyFeature' },
      'translation': { displayName: 'Translation Matching', routePath: 'translation', component: 'TranslationMatching' },
      'grammar': { displayName: 'Grammar Module', routePath: 'grammar', component: 'GrammarFeature' },
      'cloze': { displayName: 'Cloze Exercise', routePath: 'cloze', component: 'ClozeExercise' },
      'shapePuzzle': { displayName: 'Shape Puzzle', routePath: 'shapePuzzle', component: 'ShapePuzzle' },
      'complete': { displayName: 'Chapter Complete', routePath: 'complete', component: 'ChapterCompletion' }
    };

    console.log('📋 Using default module mapping');
    console.groupEnd();
    return defaultMapping;

  } catch (error) {
    console.error('❌ Error getting module mapping:', error);
    console.groupEnd();

    // Return default mapping on error
    return {
      'dialogue': { displayName: 'Dialogue Practice', routePath: 'dialogue', component: 'DialogueFeature' },
      'vocabulary': { displayName: 'Vocabulary Practice', routePath: 'vocabulary', component: 'VocabularyFeature' },
      'translation': { displayName: 'Translation Matching', routePath: 'translation', component: 'TranslationMatching' },
      'grammar': { displayName: 'Grammar Module', routePath: 'grammar', component: 'GrammarFeature' },
      'cloze': { displayName: 'Cloze Exercise', routePath: 'cloze', component: 'ClozeExercise' },
      'shapePuzzle': { displayName: 'Shape Puzzle', routePath: 'shapePuzzle', component: 'ShapePuzzle' },
      'complete': { displayName: 'Chapter Complete', routePath: 'complete', component: 'ChapterCompletion' }
    };
  }
};

// Note: useLearningModulesOrder hook moved to useDataService.js

// Note: useModulesOrderForChapter hook moved to useDataService.js

// Note: useModuleMapping hook moved to useDataService.js

// 🔍 COMPONENT DISCOVERY FUNCTIONS (Browser-Compatible)
export const discoverAvailableModules = async (componentsPath = './src/components') => {
  try {
    console.group('🔍 DataService: Loading available modules from registry');
    console.log('📋 Using browser-compatible component registry');

    const discoveredModules = scanComponentsDirectory(componentsPath);
    console.log('✅ Loaded modules from registry:', Object.keys(discoveredModules).length);

    console.groupEnd();
    return discoveredModules;
  } catch (error) {
    console.error('❌ Error loading modules from registry:', error);
    console.groupEnd();
    throw new DataServiceError(
      'Failed to load available modules from registry',
      'REGISTRY_ERROR',
      { error: error.message }
    );
  }
};

export const updateModuleMappingWithDiscovery = async (componentsPath = './src/components') => {
  try {
    console.group('🔄 DataService: Updating module mapping with registry');

    // Get current module mapping from Firebase
    const currentMapping = await getModuleMapping();
    console.log('📋 Current mapping modules:', Object.keys(currentMapping).length);

    // Load modules from registry (browser-compatible)
    const registryModules = await discoverAvailableModules(componentsPath);
    console.log('🔍 Registry modules:', Object.keys(registryModules).length);

    // Merge mappings
    const mergedMapping = mergeModuleMappings(currentMapping, registryModules);
    console.log('🔄 Merged mapping modules:', Object.keys(mergedMapping).length);

    // Validate the merged mapping
    const validation = validateModuleMapping(mergedMapping);
    if (!validation.isValid) {
      console.warn('⚠️ Module mapping validation failed:', validation.errors);
    }

    // Update Firebase with merged mapping
    const docRef = doc(db, 'feature_testing/Korea_University/LearningModulesOrder', 'order');
    const docSnap = await getDoc(docRef);

    let existingData = {};
    if (docSnap.exists()) {
      existingData = docSnap.data();
    }

    existingData.moduleMap = mergedMapping;
    existingData.lastUpdated = serverTimestamp();
    existingData.lastUpdatedBy = 'component_registry';
    existingData.registryTimestamp = new Date().toISOString();

    await setDoc(docRef, existingData, { merge: true });

    console.log('✅ Module mapping updated successfully');
    console.groupEnd();

    return {
      success: true,
      mergedMapping,
      validation,
      newModules: Object.keys(registryModules).length,
      totalModules: Object.keys(mergedMapping).length
    };

  } catch (error) {
    console.error('❌ Error updating module mapping:', error);
    console.groupEnd();
    throw new DataServiceError(
      'Failed to update module mapping with registry',
      'UPDATE_ERROR',
      { error: error.message }
    );
  }
};

export const getAvailableModulesForSelection = async () => {
  try {
    console.group('📋 DataService: Getting available modules for selection');

    // Get current module mapping
    const currentMapping = await getModuleMapping();
    console.log('📋 Current mapping modules:', Object.keys(currentMapping).length);

    // Load modules from registry (browser-compatible)
    const registryModules = await discoverAvailableModules();
    console.log('🔍 Registry modules:', Object.keys(registryModules).length);

    // Merge to get all available modules
    const allModules = mergeModuleMappings(currentMapping, registryModules);

    // Format for selection UI
    const availableModules = Object.entries(allModules).map(([moduleId, config]) => ({
      id: moduleId,
      displayName: config.displayName || moduleId,
      routePath: config.routePath || moduleId,
      component: config.component || moduleId,
      // filePath removed - was causing Webpack to create empty media files
      discovered: config.discovered || false,
      lastDiscovered: config.discoveredAt || config.lastDiscovered
    }));

    console.log('📊 Available modules for selection:', availableModules.length);
    console.groupEnd();

    return availableModules;

  } catch (error) {
    console.error('❌ Error getting available modules:', error);
    console.groupEnd();
    throw new DataServiceError(
      'Failed to get available modules for selection',
      'SELECTION_ERROR',
      { error: error.message }
    );
  }
};

// Learning Modules Order Service
export const LearningModulesOrderService = {
  getLearningModulesOrder,
  getModulesOrderForChapter,
  getModuleMapping
};

// ============================================================================
// SMART DIALOGUE SELECTOR SERVICE
// ============================================================================

/**
 * Get all available dialogue documents for a specific level and chapter
 * @param {number} level 
 * @param {number} chapter 


/**
 * Fuzzy search for dialogues based on a query string (e.g. 'intro', 'vocab')
 * @param {number} level 
 * @param {number} chapter 
 * @param {string} query 
 * @returns {Promise<Array>} List of matching dialogue documents
 */
export const fuzzySearchDialogues = async (level, chapter, query) => {
  const allDialogues = await getAvailableDialogues(level, chapter);
  if (!query) return allDialogues;

  const lowerQuery = query.toLowerCase();

  return allDialogues.filter(d => {
    // Search in ID
    if (d.id.toLowerCase().includes(lowerQuery)) return true;

    // Search in title/name fields in data
    const data = d.data || {};
    if (data.title && data.title.toLowerCase().includes(lowerQuery)) return true;
    if (data.name && data.name.toLowerCase().includes(lowerQuery)) return true;
    if (data.metadata?.title && data.metadata.title.toLowerCase().includes(lowerQuery)) return true;

    return false;
  });
};

// Export tracking system (legacy tracking exports from data/tracking.js)
export {
  Tracking,
  VocabularyTracking,
  GrammarTracking,
  DialogueTracking,
  ChapterProgress,
  ExposureTracking,
  AttemptTracking
} from './tracking.js';

// ============================================================================
// TRACKING V2 SERVICES
// ============================================================================

// Content Discovery Service
export const ContentDiscoveryServiceV2 = {
  async discoverVocabularyContent(level, chapter, institution = 'Korea_University') {
    return ContentDiscoveryService.discoverVocabularyContent(level, chapter, institution);
  },

  async discoverGrammarContent(level, chapter, institution = 'Korea_University') {
    return ContentDiscoveryService.discoverGrammarContent(level, chapter, institution);
  },

  async getContentMetadata(level, chapter, institution = 'Korea_University') {
    return ContentDiscoveryService.getContentMetadata(level, chapter, institution);
  },

  // NEW: Dialogue content discovery for scenario files
  // Filter: only feature_dialogue_uploader types (intro, vocab, comprehension, grammar, conclusion).
  // Exclude ReviewGenerator-generated content (doc ids starting with "review_").
  async discoverDialogueContent(level, chapter, institution = 'Korea_University') {
    const ALLOWED_DIALOGUE_PREFIXES = [
      'scenario-introduction',
      'scenario-vocab',
      'scenario-comprehension',
      'scenario-grammar',
      'scenario-conclusion'
    ];
    console.group(`🔍 ContentDiscoveryServiceV2: Discovering dialogue content`);
    console.log(`📊 Level: ${level}, Chapter: ${chapter}, Institution: ${institution}`);

    try {
      const collectionPath = `feature_testing/${institution}/Level${level}/Dialogue/Chapter${chapter}`;
      console.log(`🔥 Firebase path: ${collectionPath}`);

      const { getDocs, collection } = await import('firebase/firestore');
      const querySnapshot = await getDocs(collection(db, collectionPath));

      const dialogueContent = [];
      const availableTypes = [];

      querySnapshot.forEach((doc) => {
        const docId = doc.id;
        if (docId.startsWith('review_')) return;
        const allowed = ALLOWED_DIALOGUE_PREFIXES.some(prefix =>
          docId === prefix || docId.startsWith(`${prefix}-`) || docId.startsWith(`${prefix}_`)
        );
        if (!allowed) return;

        const docData = doc.data();
        dialogueContent.push({
          id: docId,
          data: docData,
          type: docId,
          title: docData.title || docId,
          totalSteps: docData.total_steps ?? 0
        });
        availableTypes.push(docId);
      });

      console.log(`✅ Discovered ${dialogueContent.length} dialogue files:`, availableTypes);
      console.log(`📋 Available types: ${availableTypes.join(', ')}`);
      console.groupEnd();

      return {
        content: dialogueContent,
        availableTypes: availableTypes,
        hasContent: dialogueContent.length > 0,
        metadata: {
          level,
          chapter,
          institution,
          collectionPath,
          discoveryTime: new Date().toISOString()
        }
      };

    } catch (error) {
      console.error(`❌ Error discovering dialogue content:`, error);
      console.groupEnd();
      return {
        content: [],
        availableTypes: [],
        hasContent: false,
        error: error.message,
        metadata: {
          level,
          chapter,
          institution,
          error: true,
          discoveryTime: new Date().toISOString()
        }
      };
    }
  },

  // NEW: Get specific dialogue content by type
  async getDialogueContent(level, chapter, dialogueType, institution = 'Korea_University') {
    console.group(`🔍 ContentDiscoveryServiceV2: Getting dialogue content`);
    console.log(`📊 Level: ${level}, Chapter: ${chapter}, Type: ${dialogueType}`);

    try {
      const docPath = `feature_testing/${institution}/Level${level}/Dialogue/Chapter${chapter}/${dialogueType}`;
      console.log(`🔥 Firebase path: ${docPath}`);

      const { getDoc, doc } = await import('firebase/firestore');
      const docRef = doc(db, docPath);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        console.log(`✅ Found dialogue content: ${dialogueType}`);
        console.log(`📋 Title: ${data.title || 'N/A'}, Steps: ${data.total_steps || 0}`);

        // ENHANCED v1.4: Validate dialogue data structure before returning
        const validatedData = validateData(data, validateDialogueData, {
          operation: 'getDialogueContent',
          firestorePath: docPath,
          level,
          chapter,
          institution,
          dialogueType
        });

        console.groupEnd();

        return {
          id: dialogueType,
          data: validatedData,
          exists: true,
          metadata: {
            level,
            chapter,
            institution,
            dialogueType,
            docPath,
            retrievalTime: new Date().toISOString()
          }
        };
      } else {
        console.log(`⚠️ Dialogue content not found: ${dialogueType}`);
        console.groupEnd();

        // ENHANCED v1.4: Throw ValidationError instead of returning null data
        const notFoundError = new ValidationError(
          `Dialogue content not found: ${dialogueType}`,
          [{ path: docPath, expected: 'document', got: 'not found', message: 'Document does not exist in Firestore' }],
          { level, chapter, institution, dialogueType, docPath }
        );

        await writeRawError(notFoundError, {
          componentName: 'ContentDiscoveryServiceV2',
          operation: 'getDialogueContent',
          firestorePath: docPath,
          level,
          chapter,
          dialogueType,
          operationType: 'document_not_found',
          severity: 'high'
        });

        throw notFoundError;
      }

    } catch (error) {
      const docPath = `feature_testing/${institution}/Level${level}/Dialogue/Chapter${chapter}/${dialogueType}`;

      await writeRawError(error, {
        componentName: 'ContentDiscoveryServiceV2',
        level,
        chapter,
        dialogueType,
        operation: 'getDialogueContent',
        attemptedPath: docPath,
        operationType: 'fetch_error',
        severity: 'high',
        isValidationError: error instanceof ValidationError || error.isValidationError
      });

      console.error(`❌ Error getting dialogue content:`, error);
      console.groupEnd();

      // ENHANCED v1.4: Re-throw error for ErrorBoundary to catch
      // Wrap in ValidationError if not already one
      if (error instanceof ValidationError || error.isValidationError) {
        throw error;
      }

      throw new ValidationError(
        `Failed to fetch dialogue content: ${error.message}`,
        [{ path: docPath, expected: 'dialogue data', got: error.message, message: 'Fetch failed' }],
        { level, chapter, institution, dialogueType, docPath, originalError: error.message }
      );
    }
  }
};

// Tracking Data Service
export const TrackingDataServiceV2 = {
  async initializeChapterTracking(userId, level, chapter, dataParameters = {}) {
    return TrackingV2Service.initializeDocuments(userId, level, chapter, dataParameters);
  },

  async updateChapterProgress(userId, level, chapter, progressData, dataParameters = {}) {
    return TrackingV2Service.markChapterStarted(userId, level, chapter, dataParameters);
  },

  async updateModuleProgress(userId, level, chapter, moduleKey, progressData, dataParameters = {}) {
    return TrackingV2Service.updateModuleProgress(userId, level, chapter, moduleKey, progressData, dataParameters);
  },

  async addQuestionAttempt(userId, level, chapter, moduleKey, questionNum, questionData, dataParameters = {}) {
    return TrackingV2Service.addQuestionAttempt(userId, level, chapter, moduleKey, questionNum, questionData, dataParameters);
  },

  async markContentExposed(userId, level, chapter, contentId, contentType, moduleKey, dataParameters = {}) {
    if (contentType === 'vocabulary') {
      return TrackingV2Service.markVocabularyExposed(userId, level, chapter, contentId, moduleKey, dataParameters);
    } else if (contentType === 'grammar') {
      return TrackingV2Service.markGrammarExposed(userId, level, chapter, contentId, moduleKey, dataParameters);
    }
  },

  async updateMasteryData(userId, level, chapter, contentId, contentType, moduleKey, masteryData, dataParameters = {}) {
    if (contentType === 'vocabulary') {
      return TrackingV2Service.updateVocabularyMastery(userId, level, chapter, contentId, moduleKey, masteryData, dataParameters);
    } else if (contentType === 'grammar') {
      return TrackingV2Service.updateGrammarMastery(userId, level, chapter, contentId, moduleKey, masteryData, dataParameters);
    }
  },

  async getChapterTrackingData(userId, level, chapter) {
    return TrackingV2Service.getChapterData(userId, level, chapter);
  },

  async getModuleTrackingData(userId, level, chapter, moduleKey) {
    const chapterData = await TrackingV2Service.getChapterData(userId, level, chapter);
    return chapterData?.learningProgress?.modules?.[moduleKey] || null;
  },

  async getQuestionTrackingData(userId, level, chapter, moduleKey, questionNum) {
    const chapterData = await TrackingV2Service.getChapterData(userId, level, chapter);
    return chapterData?.learningProgress?.modules?.[moduleKey]?.questions?.[questionNum] || null;
  }
};

// Data Parameters Service
export const DataParametersServiceV2 = {
  async updateChapterParameters(userId, level, chapter, parameters) {
    return silentTrack(async () => {
      const progressRef = doc(db, `users/${userId}/level_${level}/chapter_${chapter}/Learn/LearningProgress`);
      await updateDoc(progressRef, {
        parameters: parameters,
        lastUpdated: new Date().toISOString()
      });
      console.log(`✓ Chapter parameters updated for Level ${level} Chapter ${chapter}`);
    }, 'update_chapter_parameters');
  },

  async updateModuleParameters(userId, level, chapter, moduleKey, parameters) {
    return silentTrack(async () => {
      const progressRef = doc(db, `users/${userId}/level_${level}/chapter_${chapter}/Learn/LearningProgress`);
      await updateDoc(progressRef, {
        [`modules.${moduleKey}.parameters`]: parameters,
        [`modules.${moduleKey}.lastUpdated`]: new Date().toISOString()
      });
      console.log(`✓ Module parameters updated for ${moduleKey}`);
    }, 'update_module_parameters');
  },

  async updateQuestionParameters(userId, level, chapter, moduleKey, questionNum, parameters) {
    return silentTrack(async () => {
      const progressRef = doc(db, `users/${userId}/level_${level}/chapter_${chapter}/Learn/LearningProgress`);
      await updateDoc(progressRef, {
        [`modules.${moduleKey}.questions.${questionNum}.parameters`]: parameters,
        [`modules.${moduleKey}.questions.${questionNum}.lastUpdated`]: new Date().toISOString()
      });
      console.log(`✓ Question parameters updated for ${moduleKey} Q${questionNum}`);
    }, 'update_question_parameters');
  },

  async getChapterParameters(userId, level, chapter) {
    return silentTrack(async () => {
      const progressRef = doc(db, `users/${userId}/level_${level}/chapter_${chapter}/Learn/LearningProgress`);
      const snap = await getDoc(progressRef);
      return snap.exists() ? snap.data().parameters || {} : {};
    }, 'get_chapter_parameters');
  },

  async getModuleParameters(userId, level, chapter, moduleKey) {
    return silentTrack(async () => {
      const progressRef = doc(db, `users/${userId}/level_${level}/chapter_${chapter}/Learn/LearningProgress`);
      const snap = await getDoc(progressRef);
      return snap.exists() ? snap.data().modules?.[moduleKey]?.parameters || {} : {};
    }, 'get_module_parameters');
  },

  async getQuestionParameters(userId, level, chapter, moduleKey, questionNum) {
    return silentTrack(async () => {
      const progressRef = doc(db, `users/${userId}/level_${level}/chapter_${chapter}/Learn/LearningProgress`);
      const snap = await getDoc(progressRef);
      return snap.exists() ? snap.data().modules?.[moduleKey]?.questions?.[questionNum]?.parameters || {} : {};
    }, 'get_question_parameters');
  },

  /**
   * Get comprehension data
   */
  async getComprehensionData(level, chapter, comprehensionId, institution = 'Korea_University') {
    return silentTrack(async () => {
      const docPath = ['feature_testing', institution, `Level${level}`, 'Comprehension', `Chapter${chapter}`, comprehensionId];
      const ref = doc(db, ...docPath);
      const snap = await getDoc(ref);

      if (snap.exists()) {
        return snap.data();
      }
      return null;
    }, 'get_comprehension_data');
  },

  /**
   * Update comprehension mastery
   */
  async updateComprehensionMastery(userId, level, chapter, comprehensionId, moduleKey, masteryData) {
    return TrackingV2Service.updateComprehensionMastery(userId, level, chapter, comprehensionId, moduleKey, masteryData);
  },

  async markDialogueExposed(userId, level, chapter, dialogueId, moduleKey, dataParameters = {}) {
    return TrackingV2Service.markDialogueExposed(userId, level, chapter, dialogueId, moduleKey, dataParameters);
  },

  async updateDialogueMastery(userId, level, chapter, dialogueId, moduleKey, masteryData, dataParameters = {}) {
    return TrackingV2Service.updateDialogueMastery(userId, level, chapter, dialogueId, moduleKey, masteryData, dataParameters);
  },

  async markIntroDialogueExposed(userId, level, chapter, dialogueId, moduleKey, dataParameters = {}) {
    return TrackingV2Service.markIntroDialogueExposed(userId, level, chapter, dialogueId, moduleKey, dataParameters);
  },

  async updateIntroDialogueMastery(userId, level, chapter, dialogueId, moduleKey, masteryData, dataParameters = {}) {
    return TrackingV2Service.updateIntroDialogueMastery(userId, level, chapter, dialogueId, moduleKey, masteryData, dataParameters);
  },

  async markConclusionDialogueExposed(userId, level, chapter, dialogueId, moduleKey, dataParameters = {}) {
    return TrackingV2Service.markConclusionDialogueExposed(userId, level, chapter, dialogueId, moduleKey, dataParameters);
  },

  async updateConclusionDialogueMastery(userId, level, chapter, dialogueId, moduleKey, masteryData, dataParameters = {}) {
    return TrackingV2Service.updateConclusionDialogueMastery(userId, level, chapter, dialogueId, moduleKey, masteryData, dataParameters);
  }
};

// ============================================================================
// DIALOGUE DISCOVERY SERVICE
// ============================================================================

/**
 * Get all available dialogue documents for a specific level and chapter, and checks the legacy location if needed.
 * Used for the "Select Module" fallback UI
 * 
 * @param {number} level 
 * @param {number} chapter 
 * @returns {Promise<Array>} List of available dialogue documents
 */
export const getAvailableDialogues = async (level, chapter) => {
  try {
    console.group(`🔍 Getting available dialogues for Level ${level} Chapter ${chapter}`);

    // Construct collection path: feature_testing/Korea_University/LevelX/Dialogue/ChapterY
    const collectionPath = `feature_testing/Korea_University/Level${level}/Dialogue/Chapter${chapter}`;
    console.log('📂 Collection path:', collectionPath);

    // Prepare result array
    let dialogues = [];

    // 1. Try Primary Path
    try {
      const collectionRef = collection(db, collectionPath);
      const snapshot = await getDocs(collectionRef);

      if (!snapshot.empty) {
        const docs = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            path: doc.ref.path,
            title: data.title || data.topic || doc.id,
            type: data.type || 'unknown',
            steps: data.steps ? Object.keys(data.steps).length : 0,
            lastUpdated: data.last_updated || data.timestamp
          };
        });
        dialogues = [...dialogues, ...docs];
      }
    } catch (e) {
      console.warn('Primary path scan failed', e);
    }

    // 2. Try Legacy Path (Level2/Chapter1 often has everything in dev)
    // Only if we didn't search it already
    if (level !== 2 || chapter !== 1) {
      try {
        const legacyPath = `feature_testing/Korea_University/Level2/Dialogue/Chapter1`;
        const legacyRef = collection(db, legacyPath);
        const legacySnap = await getDocs(legacyRef);

        if (!legacySnap.empty) {
          const legacyDocs = legacySnap.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              path: doc.ref.path,
              title: `(Legacy L2C1) ${data.title || data.topic || doc.id}`,
              type: data.type || 'unknown',
              steps: data.steps ? Object.keys(data.steps).length : 0,
              lastUpdated: data.last_updated || data.timestamp
            };
          });
          dialogues = [...dialogues, ...legacyDocs];
        }
      } catch (e) {
        console.warn('Legacy path scan failed', e);
      }
    }

    console.log(`✅ Found ${dialogues.length} dialogues`);
    console.groupEnd();
    return dialogues;

  } catch (error) {
    console.error('❌ Error getting available dialogues:', error);
    console.groupEnd();
    return [];
  }
};

// Add to main DataService export
const DataService = {
  GrammarService,
  VocabularyService,
  ExerciseService,
  ProgressService,
  ContentService,
  DataUtils,
  UserGrammarTracking,
  LearningModulesOrderService,
  db,
  auth,

  // Tracking V2 Services
  ContentDiscoveryServiceV2,
  TrackingDataServiceV2,
  DataParametersServiceV2,

  // Offline system exports
  OfflineStorage,
  OfflineProgressQueue,
  OfflineContentCache,
  OfflineSync,
  OfflineIndicators,

  // Offline utility functions
  isOnline,
  isFirebaseAvailable,

  // Offline management functions
  initializeOfflineSystem,
  getOfflineFallback
};

export { DataService };
export default DataService;
